import WMTS from "ol/source/WMTS";
import OSM from "ol/source/OSM";
import Group, {Options as olGroupOptions} from 'ol/layer/Group';
import Vector from "ol/layer/Vector";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import TileLayer from "ol/layer/Tile";
import TileState from 'ol/TileState';
import ImageLayer from "ol/layer/Image";
import Image from "ol/layer/Image";
import TileArcGISRest from 'ol/source/TileArcGISRest'
import TileWMS from "ol/source/TileWMS";
import ImageWMS from 'ol/source/ImageWMS';
import Cluster from "ol/source/Cluster";
import WebGLVectorLayer from 'ol/layer/WebGLVector';
import {buffer, createEmpty, extend, Extent, getCenter, isEmpty} from "ol/extent";
import {bbox as bboxStrategy} from 'ol/loadingstrategy';
import GeoJSON from "ol/format/GeoJSON";
import WKT from "ol/format/WKT";
import XYZ from 'ol/source/XYZ';
import {Circle, Geometry, LineString, MultiLineString, MultiPoint, MultiPolygon, Polygon} from "ol/geom";
import Feature, {FeatureLike} from "ol/Feature";
import {get as getOlProjection, getPointResolution, ProjectionLike, transform, transformExtent} from "ol/proj";
import Projection from "ol/proj/Projection";
import {getLength, SphereMetricOptions} from "ol/sphere";
import {ImageArcGISRest, ImageStatic, Source} from "ol/source";
import GML3 from 'ol/format/GML3';
import GIS from "./app"
import { GeoJSON as GeoJSONData } from "geojson";
import {
    AuthenticationScheme,
    GeometryType,
    LayerConfiguration,
    LayerType,
    Legend,
    StyleDependency,
    SwitcherActivation,
    Toggle, WFSLayerConfiguration,
    WMSPARAMS,
    WMSTiledGroupLayerConfiguration,
    WMSTiledGroupSubLayerConfiguration
} from "./configuration/layer";
import ImageSource from "ol/source/Image";
import {
    Attribute, Attributes,
    FeatureInExtent,
    FeatureInfoCallback,
    GeometryString,
    GetExtent, GetFeaturesInExtent, GetGeometry, GetGroupExtent,
    GetIDs,
    GetXYM,
    Opacity,
    StyleNameString,
    Visible, ZIndex
} from "./general/callback";
import {ObjectEvent} from "ol/Object";
import {getIdProperty, getFeaturesFromSource, CURRENT_STYLENAME_PROPERTY} from "./function/layer";
import {
    AuthenticationOptions,
    EPSGOptions,
    FeatureInfoOptions,
    FetchOptions,
    FormatAndEPSGOptions,
    GetFeatureByIDOptions,
    GetFeatureCenterByIDOptions,
    GetFeatureExtentByIDOptions,
    GetFeatureIDsAtCoordinateOptions,
    GetFeatureIDsByAttributeOptions,
    GetFeaturesIDsInExtentOptions,
    GetFeaturesIDsInRadiusOptions,
    GetFeaturesInExtentOptions,
    GetIdPropertyOptions,
    LayerIdWithDistance,
    NewSourceOptions, RewriteOptions
} from "./general/options";
import BaseLayer from "ol/layer/Base";
import {Coordinate} from "ol/coordinate";
import Point from "ol/geom/Point";
import {GroupConfiguration} from "./configuration/group";
import TileSource from "ol/source/Tile";
import BaseVectorLayer from "ol/layer/BaseVector";
import {Collection, ImageTile, Tile} from "ol";
import {StyleLike} from "ol/style/Style";
import {LoadFunction} from "ol/Tile";
import {FeatureLoader} from "ol/featureloader";
import {WriteOptions} from "ol/format/Feature";
import LayersControl from "./controls/layers.ts";
import {GeoJSONWithCRS} from "./general/general.ts";
import GeoJSONCRS from "./general/crs.ts";

class AttributeMissingError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "AttributeMissingError";
    }
}

class Layer {

    private readonly GIS;

    private layer: { [key: string]: LayerType } = {};
    private basemaps: LayerType[] = [];
    private group: { [key: string]: Group } = {};
    public groupEntries: Group[] = [];
    private rasterFailed: {
        [key: string]:
            | (ImageTile & { [key: string]: any })
            | (HTMLImageElement & { [key: string]: any });
    } = {};
    private rasterRetries: { [key: string]: number } = {};
    private vectorFailed: { [key: string]: VectorSource<Feature<Geometry>> } = {};
    private vectorRetries: { [key: string]: number } = {};
    /**
     * Requests the map gave up on: reported and no longer retried
     * automatically, so they stop holding {@link getNumberOfFailed} above
     * zero. {@link fetchFailed} takes them back into the retry flow — the
     * hosting application can still recover them after a token refresh.
     */
    private rasterAbandoned: {
        [key: string]:
            | (ImageTile & { [key: string]: any })
            | (HTMLImageElement & { [key: string]: any });
    } = {};
    private vectorAbandoned: { [key: string]: VectorSource<Feature<Geometry>> } = {};
    private tileLoadErrorHandlers: Map<Source, {
        tileErrorHandler: (evt: any) => void;
        imageErrorHandler: (evt: any) => void;
        tileEndHandler: (evt: any) => void;
        imageEndHandler: (evt: any) => void;
    }> = new Map();
    /**
     * TEMPORARY WORKAROUND (task #193100) — guards the one-per-session console
     * warning emitted the first time a layer configures the mobile
     * tile-performance overrides (`preload` / `cacheSize` / `interpolate`).
     * To be removed together with those overrides once the upstream OpenLayers
     * fix ships.
     */
    private temporaryTilePerformanceOverrideWarned = false;
    private onlineHandler?: () => void;
    private offlineHandler?: () => void;
    private listenersAttached = false;
    private startRetryWatcherScheduled = false;
    private onlineOnceHandler: (() => void) | null = null;
    private requestFailedCallback: RequestFailedCallback | null = null;
    /** Dedup keys (`type|layerName|status`) already reported to the request-failed callback. */
    private notifiedFailures: Set<string> = new Set();

    public type = {
        TILEARCGISREST: "tileArcGISRest",
        IMAGEARCGISREST: "imageArcGISRest",
        IMAGESTATIC: "imageStatic",
        WMTS: "wmts",
        WMSTILED: "wmsTiled",
        WMSTILEDGROUP: "wmsTiledGroup",
        WMS: "wms",
        OSM: "osm",
        GEOLOCATION: "geolocation",
        GEOJSON: "geojson",
        WEBGLPOINTS: "webglpoints",
        CLUSTER: "cluster",
        WFS: "wfs",
        XYZ: "xyz"
    } as const;

    public format = {
        GEOJSON: "geojson",
        WKT: "wkt",
        XY: "xy",
        SIMPLE: "simple",
        GML3: "gml3"
    }

    private retryDelays = [2000, 6000, 12000, 60000]; // 2s, 6s, 12s, 60s
    /**
     * Used instead of {@link retryDelays} while a burst is converging — the rest
     * of the map loaded and only a small residue is still failing. Waiting a
     * minute for the last attempt of two stragglers only delays the answer the
     * hosting application is waiting for, so the sequence finishes in ~15s
     * instead of ~80s. A full outage still uses the slow ladder.
     */
    private fastRetryDelays = [1000, 2000, 4000, 8000]; // 1s, 2s, 4s, 8s
    /** At or below this many pending requests a burst counts as converging. */
    private FASTRETRYRESIDUE = 5;
    /** Pending count of the most recent accurate measurement (before a pass). */
    private pendingSnapshot = 0;
    /** Highest pending count seen in the current burst. */
    private burstPeak = 0;
    private retryWatcherTimer: number | null = null;
    private retryAttempt = 0;
    private retryRunId = 0;
    private activeRunId: number | 0 = 0;
    /**
     * Latched once the automatic sequence walked through every entry of
     * {@link retryDelays}. While it is set no new automatic cycle may start, so
     * the sequence terminates deterministically instead of restarting at 1/4
     * whenever a late failure trickles in. Cleared when every collected failure
     * recovered, or by {@link fetchFailed} / {@link removeFailedRasterAndVector}.
     */
    private retryCycleExhausted = false;
    /** URLs already reported as given up on, so each is warned about once. */
    private unfetchableWarned: Set<string> = new Set();
    /**
     * Armed while failures that look like a lost connection are collected. The
     * `online` event only tracks the network interface, so a connection lost at
     * the VPN, proxy or server level never fires it — the probe re-requests one
     * of the failed URLs on an interval to notice the connection coming back.
     */
    private reconnectRecoveryArmed = false;
    private reconnectProbeTimer: number | null = null;
    private RECONNECTPROBEINTERVAL = 15000;
    private IMAGELAYERNAMEPROPERTY: string = 'LAYERNAME';
    private TILERETRIES = 4;
    private TILEREVOKE = 5000;

    private DEFAULT_STYLENAME = 'defaultStyleName';
    private CURRENT_STYLENAME = CURRENT_STYLENAME_PROPERTY;
    private ACTIVE_STYLENAME = 'activeStyleName';

    private RETRYCODES = [408, 429, 500, 502, 503, 504];

    /**
     * Authorization scheme used when a layer configuration or a
     * {@link Layer.setLayerLoader} call does not specify one. Keeps existing
     * configurations working unchanged.
     */
    private DEFAULTAUTHENTICATIONSCHEME: AuthenticationScheme = 'Basic';

    constructor(GIS: GIS) {
        this.GIS = GIS;
    }

    public init(layerConfiguration: LayerConfiguration[], groupConfiguration: GroupConfiguration[]) {
        const lastCreation: LayerConfiguration[] = []
        layerConfiguration.map(layer => {
            //CLUSTER needs already created a source
            layer.type === this.type.CLUSTER ? lastCreation.push(layer) : this.create(layer);
        });

        lastCreation.map(layer => {
            this.create(layer)
        });

        this.setStyleDependencies(layerConfiguration);

        this.initGroup(groupConfiguration);

        this.attachOnlineOfflineListeners();
    }

    /**
     * Create layer programmatically for testing
     */
    public create(layer: LayerConfiguration) {
        this.warnTemporaryTilePerformanceOverrideOnce(layer);
        switch (layer.type) {
            case this.type.IMAGEARCGISREST:
                this.layer[layer.name] = new Image({
                    className: layer.type + '-' + layer.name,
                    name: layer.name,
                    visible: layer.visible,
                    base: layer.base,
                    opacity: layer.opacity,
                    extent: layer.extent,
                    source: new ImageArcGISRest({
                        url: layer.url,
                        params: {
                            'LAYERS': layer.layers,
                            'FORMAT': layer.format,
                            'TOKEN': layer.token
                        },
                        projection: this.GIS.Projection.getRegistered(layer.epsg),
                        interpolate: layer.interpolate,
                        crossOrigin: "anonymous"
                    }),
                    styleDependency: layer.styleDependency,
                    switcher: layer.switcher,
                    symbology: layer.symbology,
                    zIndex: layer.zIndex,
                    minZoom: layer.minZoom,
                    maxZoom: layer.maxZoom,
                    minResolution: layer.minResolution,
                    maxResolution: layer.maxResolution,
                    zoomExtent: layer.zoomExtent
                } as import("ol/layer/BaseImage").Options<ImageSource> | undefined)
                if (layer.authentication) {
                    this.setLayerLoader(layer.name, layer.authentication, {scheme: layer.authenticationScheme});
                } else {
                    this.attachImageSourceTrackingLoader(this.layer[layer.name].getSource(), layer.name);
                    this.attachLoadHandlersToSource(this.layer[layer.name].getSource());
                }
                break;
            case this.type.TILEARCGISREST:
                this.layer[layer.name] = new TileLayer({
                    source: new TileArcGISRest({
                        url: layer.url,
                        projection: this.GIS.Projection.getRegistered(layer.epsg),
                        params: {
                            'LAYERS': layer.layers,
                            'FORMAT': layer.format,
                            'TOKEN': layer.token
                        },
                        tileGrid: this.GIS.Projection.grid[layer.matrixSet],
                        interpolate: layer.interpolate,
                        wrapX: layer.wrapX
                    }),
                    preload: layer.preload,
                    cacheSize: layer.cacheSize,
                    opacity: layer.opacity,
                    visible: layer.visible,
                    base: layer.base ? layer.base : undefined,
                    name: layer.name,
                    className: layer.type + '-' + layer.name,
                    switcher: layer.switcher,
                    symbology: layer.symbology,
                    zIndex: layer.zIndex,
                    minZoom: layer.minZoom,
                    maxZoom: layer.maxZoom,
                    minResolution: layer.minResolution,
                    maxResolution: layer.maxResolution,
                    extent: layer.extent,
                    zoomExtent: layer.zoomExtent
                } as import("ol/layer/BaseTile").Options<TileSource> | undefined)
                if (layer.authentication) {
                    this.setLayerLoader(layer.name, layer.authentication, {scheme: layer.authenticationScheme});
                } else {
                    this.attachImageSourceTrackingLoader(this.layer[layer.name].getSource(), layer.name);
                    this.attachLoadHandlersToSource(this.layer[layer.name].getSource());
                }
                break;
            case this.type.IMAGESTATIC:
                this.layer[layer.name] = new ImageLayer({
                    className: layer.type + '-' + layer.name,
                    name: layer.name,
                    visible: layer.visible,
                    base: layer.base,
                    opacity: layer.opacity,
                    source: new ImageStatic({
                        url: layer.url,
                        imageExtent: layer.extent,
                        projection: this.GIS.Projection.getRegistered(layer.epsg)
                    }),
                    styleDependency: layer.styleDependency,
                    switcher: layer.switcher,
                    symbology: layer.symbology,
                    zIndex: layer.zIndex,
                    minZoom: layer.minZoom,
                    maxZoom: layer.maxZoom,
                    minResolution: layer.minResolution,
                    maxResolution: layer.maxResolution,
                    zoomExtent: layer.zoomExtent
                } as import("ol/layer/BaseImage").Options<ImageSource> | undefined)
                break;
            case this.type.XYZ:
                this.layer[layer.name] = new TileLayer({
                    source: this.createRasterSource(layer),
                    preload: layer.preload,
                    cacheSize: layer.cacheSize,
                    opacity: layer.opacity,
                    visible: layer.visible,
                    base: layer.base,
                    name: layer.name,
                    className: layer.type + '-' + layer.name,
                    switcher: layer.switcher,
                    symbology: layer.symbology,
                    zIndex: layer.zIndex,
                    minZoom: layer.minZoom,
                    maxZoom: layer.maxZoom,
                    minResolution: layer.minResolution,
                    maxResolution: layer.maxResolution,
                    extent: layer.extent,
                    zoomExtent: layer.zoomExtent
                } as import("ol/layer/BaseTile").Options<TileSource> | undefined);
                if (layer.authentication) {
                    this.setLayerLoader(layer.name, layer.authentication, {scheme: layer.authenticationScheme});
                } else {
                    this.attachImageSourceTrackingLoader(this.layer[layer.name].getSource(), layer.name);
                    this.attachLoadHandlersToSource(this.layer[layer.name].getSource());
                }
                break;
            case this.type.OSM:
                this.layer[layer.name] = new TileLayer({
                    name: layer.name,
                    source: new OSM({ interpolate: layer.interpolate }),
                    preload: layer.preload,
                    cacheSize: layer.cacheSize,
                    visible: layer.visible,
                    base: layer.base,
                    className: layer.type + '-' + layer.name,
                    switcher: layer.switcher,
                    symbology: layer.symbology,
                    zIndex: layer.zIndex,
                    minZoom: layer.minZoom,
                    maxZoom: layer.maxZoom,
                    minResolution: layer.minResolution,
                    maxResolution: layer.maxResolution,
                    zoomExtent: layer.zoomExtent
                } as import("ol/layer/BaseTile").Options<TileSource> | undefined);
                if (layer.authentication) {
                    this.setLayerLoader(layer.name, layer.authentication, {scheme: layer.authenticationScheme});
                } else {
                    this.attachImageSourceTrackingLoader(this.layer[layer.name].getSource(), layer.name);
                    this.attachLoadHandlersToSource(this.layer[layer.name].getSource());
                }
                break;
            case this.type.WMTS:
                this.layer[layer.name] = new TileLayer({
                    source: new WMTS({
                        urls: layer.urls,
                        layer: layer.layername ? layer.layername : layer.name,
                        matrixSet: layer.matrixSet ?? "none",
                        format: layer.format,
                        projection: this.GIS.Projection.getRegistered(layer.epsg),
                        tileGrid: this.GIS.Projection.grid[layer.matrixSet ?? "none"],
                        style: layer.style ? layer.style : '',
                        interpolate: layer.interpolate,
                        requestEncoding: layer.requestEncoding
                    }),
                    preload: layer.preload ?? Infinity,
                    cacheSize: layer.cacheSize,
                    opacity: layer.opacity,
                    visible: layer.visible,
                    base: layer.base,
                    name: layer.name,
                    className: layer.type + '-' + layer.name,
                    switcher: layer.switcher,
                    symbology: layer.symbology,
                    zIndex: layer.zIndex,
                    minZoom: layer.minZoom,
                    maxZoom: layer.maxZoom,
                    minResolution: layer.minResolution,
                    maxResolution: layer.maxResolution,
                    zoomExtent: layer.zoomExtent
                } as import("ol/layer/BaseTile").Options<TileSource> | undefined);
                if (layer.authentication) {
                    this.setLayerLoader(layer.name, layer.authentication, {scheme: layer.authenticationScheme});
                } else {
                    this.attachImageSourceTrackingLoader(this.layer[layer.name].getSource(), layer.name);
                    this.attachLoadHandlersToSource(this.layer[layer.name].getSource());
                }
                break;
            case this.type.WMSTILED:

                const wmsParams: WMSPARAMS = {
                    'LAYERS': layer.layers,
                    'TILED': layer.tiled,
                    'FORMAT': layer.format,
                    'VERSION': layer.version,
                    'STYLES': layer.styles ? layer.styles : "",
                    'ENV': layer.env ? layer.env : "",
                    'TOKEN': layer.token
                };

                if (parseFloat(layer.version) >= 1.3 && layer.epsg) {
                    wmsParams['SRS'] = "EPSG:" + String(layer.epsg);
                }

                this.layer[layer.name] = new TileLayer({
                    className: layer.type + '-' + layer.name,
                    name: layer.name,
                    visible: layer.visible,
                    base: layer.base,
                    opacity: layer.opacity,
                    preload: layer.preload,
                    cacheSize: layer.cacheSize,
                    source: new TileWMS({
                        url: layer.url,
                        params: wmsParams,
                        projection: this.GIS.Projection.getRegistered(layer.epsg),
                        serverType: layer.serverType,
                        tileGrid: layer.matrixSet ? this.GIS.Projection.grid[layer.matrixSet] : undefined,
                        transition: layer.transition,
                        interpolate: layer.interpolate,
                        crossOrigin: "anonymous"
                    }),
                    styleDependency: layer.styleDependency,
                    switcher: layer.switcher,
                    symbology: layer.symbology,
                    zIndex: layer.zIndex,
                    minZoom: layer.minZoom,
                    maxZoom: layer.maxZoom,
                    minResolution: layer.minResolution,
                    maxResolution: layer.maxResolution,
                    zoomExtent: layer.zoomExtent
                } as import("ol/layer/BaseTile").Options<TileSource> | undefined);
                if (layer.authentication) {
                    this.setLayerLoader(layer.name, layer.authentication, {scheme: layer.authenticationScheme});
                } else {
                    this.attachImageSourceTrackingLoader(this.layer[layer.name].getSource(), layer.name);
                    this.attachLoadHandlersToSource(this.layer[layer.name].getSource());
                }
                break;
            case this.type.WMSTILEDGROUP:
                this.createWmsTiledGroupLayer(layer);
                break;
            case this.type.WMS:
                this.layer[layer.name] = new Image({
                    className: layer.type + '-' + layer.name,
                    name: layer.name,
                    visible: layer.visible,
                    base: layer.base,
                    opacity: layer.opacity,
                    extent: layer.extent,
                    source: new ImageWMS({
                        url: layer.url,
                        params: {
                            'LAYERS': layer.layers,
                            'TILED': layer.tiled,
                            'FORMAT': layer.format,
                            'VERSION': layer.version,
                            'STYLES': layer.styles ? layer.styles : "",
                            'ENV': layer.env ? layer.env : "",
                            'TOKEN': layer.token
                        },
                        projection: this.GIS.Projection.getRegistered(layer.epsg),
                        serverType: layer.serverType,
                        interpolate: layer.interpolate,
                        crossOrigin: "anonymous"
                    }),
                    styleDependency: layer.styleDependency,
                    switcher: layer.switcher,
                    symbology: layer.symbology,
                    zIndex: layer.zIndex,
                    minZoom: layer.minZoom,
                    maxZoom: layer.maxZoom,
                    minResolution: layer.minResolution,
                    maxResolution: layer.maxResolution,
                    zoomExtent: layer.zoomExtent,
                } as import("ol/layer/BaseImage").Options<ImageSource> | undefined);
                if (layer.authentication) {
                    this.setLayerLoader(layer.name, layer.authentication, {scheme: layer.authenticationScheme});
                } else {
                    this.attachImageSourceTrackingLoader(this.layer[layer.name].getSource(), layer.name);
                    this.attachLoadHandlersToSource(this.layer[layer.name].getSource());
                }
                break;
            case this.type.GEOLOCATION:
                this.layer[layer.name] = new Vector({
                    name: layer.name,
                    source: new VectorSource(),
                    visible: layer.visible,
                    base: layer.base,
                    useSpatialIndex: false,
                    defaultStyleName: layer.style,
                    style: layer.style ? this.GIS.Style.getStyle(layer.style) : undefined,
                    styleDependency: layer.styleDependency,
                    switcher: layer.switcher,
                    symbology: layer.symbology,
                    zIndex: layer.zIndex,
                    minZoom: layer.minZoom,
                    maxZoom: layer.maxZoom,
                    minResolution: layer.minResolution,
                    maxResolution: layer.maxResolution,
                    zoomExtent: layer.zoomExtent
                } as import("ol/layer/Vector.js").Options<VectorSource<Feature<Geometry>>> | undefined);
                if (layer.base) {
                    this.basemaps.push(this.layer[layer.name])
                }
                break;

            case this.type.WEBGLPOINTS:
                this.layer[layer.name] = new WebGLVectorLayer({
                    name: layer.name,
                    source: new VectorSource(),
                    style: layer.style ? this.GIS.Style.getStyle(layer.style) : undefined,
                    styleDependency: layer.styleDependency,
                    disableHitDetection: true
                } as import("ol/layer/WebGLVector").Options<VectorSource<FeatureLike>>);
                break;
            case this.type.GEOJSON:
                this.layer[layer.name] = new Vector({
                    name: layer.name,
                    zoomExtent: layer.zoomExtent,
                    source: new VectorSource(),
                    shape: layer.shape,
                    visible: layer.visible,
                    base: layer.base,
                    create: layer.create,
                    modify: layer.modify,
                    opacity: layer.opacity,
                    defaultStyleName: layer.style,
                    style: layer.style ? this.GIS.Style.getStyle(layer.style) : undefined,
                    styleDependency: layer.styleDependency,
                    editStyle: layer.editStyle,
                    switcher: layer.switcher,
                    symbology: layer.symbology,
                    zIndex: layer.zIndex,
                    minZoom: layer.minZoom,
                    maxZoom: layer.maxZoom,
                    minResolution: layer.minResolution,
                    maxResolution: layer.maxResolution,
                    declutter: layer.declutter
                } as import("ol/layer/Vector.js").Options<VectorSource<Feature<Geometry>>> | undefined);
                break;
            case this.type.WFS:
                let olFormat;
                let format = "";
                if (layer.format === 'geojson') {
                    olFormat = new GeoJSON();
                    format = 'application/json';
                } else {
                    throw new Error('WFS format has to be specified.');
                }

                this.layer[layer.name] = new Vector({
                    name: layer.name,
                    source: new VectorSource<FeatureLike>({
                        format: olFormat,
                        url: extent => {
                            (this.layer[layer.name] as LayerType & {
                                fullPath: string
                            }).fullPath = layer.src + '&outputFormat=' + format + '&srsname=EPSG:' + String(layer.epsg) + '&' + this.createCQLFilter(layer, extent);
                            return layer.src + '&outputFormat=' + format + '&srsname=EPSG:' + String(layer.epsg) + '&' + this.createCQLFilter(layer, extent);
                        },
                        strategy: bboxStrategy
                    }) as VectorSource<FeatureLike>,
                    fullPath: layer.src + '&outputFormat=' + format + '&srsname=EPSG:' + String(layer.epsg),
                    shape: layer.shape,
                    visible: layer.visible,
                    base: layer.base,
                    create: layer.create,
                    modify: layer.modify,
                    defaultStyleName: layer.style,
                    style: layer.style ? this.GIS.Style.getStyle(layer.style) : undefined,
                    styleDependency: layer.styleDependency,
                    editStyle: layer.editStyle,
                    switcher: layer.switcher,
                    symbology: layer.symbology,
                    zIndex: layer.zIndex,
                    minZoom: layer.minZoom,
                    maxZoom: layer.maxZoom,
                    minResolution: layer.minResolution,
                    maxResolution: layer.maxResolution,
                    zoomExtent: layer.zoomExtent
                } as import("ol/layer/Vector.js").Options<VectorSource<FeatureLike>> | undefined);
                if (layer.authentication) {
                    this.setLayerLoader(layer.name, layer.authentication, {scheme: layer.authenticationScheme});
                } else {
                    this.attachWfsSourceTracking(this.layer[layer.name].getSource(), layer.name);
                    this.attachLoadHandlersToSource(this.layer[layer.name].getSource());
                }
                break;
            case this.type.CLUSTER:
                const source: VectorSource<Feature<Geometry>> | undefined = layer.source ? this.layer[layer.source].getSource() as VectorSource<Feature<Geometry>> | undefined : undefined;

                const clusterFilters = layer.cluster?.filters;
                const representativePoint = layer.cluster?.representativePoint ?? 'interiorPoint';
                const allowedGeometryTypes = layer.cluster?.geometryTypes;

                const featurePassesFilters = (feature: Feature): boolean => {
                    if (!clusterFilters || clusterFilters.length === 0) return true;
                    for (const filter of clusterFilters) {
                        const featureValue = String(feature.get(filter.attribute));
                        switch (filter.type) {
                            case "equal":
                                if (featureValue !== String(filter.value)) return false;
                                break;
                            case "not-equal":
                                if (featureValue === String(filter.value)) return false;
                                break;
                            case "includes":
                                if (typeof filter.value !== 'string' || !filter.value.includes(featureValue)) return false;
                                break;
                        }
                    }
                    return true;
                };

                const geometryToClusterPoint = (geometry: Geometry): Point => {
                    const geometryType = geometry.getType();

                    if (geometryType === 'Point') {
                        return geometry as Point;
                    }

                    if (representativePoint === 'firstCoordinate') {
                        const firstCoordinate = (geometry as any).getFirstCoordinate?.();
                        if (firstCoordinate && firstCoordinate.length >= 2) return new Point(firstCoordinate);
                    }

                    if (representativePoint === 'interiorPoint') {
                        if (geometryType === 'Polygon') {
                            return (geometry as Polygon).getInteriorPoint();
                        }
                        if (geometryType === 'MultiPolygon') {
                            // Pick the interior point of the largest sub-polygon by area.
                            const polygons = (geometry as MultiPolygon).getPolygons();
                            if (polygons.length > 0) {
                                let largestPolygon = polygons[0];
                                let largestArea = largestPolygon.getArea();
                                for (let i = 1; i < polygons.length; i++) {
                                    const area = polygons[i].getArea();
                                    if (area > largestArea) {
                                        largestArea = area;
                                        largestPolygon = polygons[i];
                                    }
                                }
                                return largestPolygon.getInteriorPoint();
                            }
                        }
                        if (geometryType === 'LineString') {
                            const line = geometry as LineString;
                            const midCoordinate = line.getCoordinateAt(0.5);
                            if (midCoordinate && midCoordinate.length >= 2) return new Point(midCoordinate);
                        }
                        if (geometryType === 'MultiLineString') {
                            // Pick the midpoint of the longest line.
                            const lines = (geometry as MultiLineString).getLineStrings();
                            if (lines.length > 0) {
                                let longestLine = lines[0];
                                let longestLength = longestLine.getLength();
                                for (let i = 1; i < lines.length; i++) {
                                    const length = lines[i].getLength();
                                    if (length > longestLength) {
                                        longestLength = length;
                                        longestLine = lines[i];
                                    }
                                }
                                const midCoordinate = longestLine.getCoordinateAt(0.5);
                                if (midCoordinate && midCoordinate.length >= 2) return new Point(midCoordinate);
                            }
                        }
                        if (geometryType === 'MultiPoint') {
                            const multiPoint = geometry as MultiPoint;
                            const points = multiPoint.getCoordinates();
                            if (points.length > 0) return new Point(points[0]);
                        }
                        if (geometryType === 'Circle') {
                            return new Point((geometry as Circle).getCenter());
                        }
                    }

                    // Fallback (and 'centroid' strategy): center of the geometry's extent.
                    const extent = geometry.getExtent();
                    return new Point(getCenter(extent));
                };

                const emptyPoint = new Point([]);

                const geometryFunction = (feature: Feature): Point => {
                    const geometry = feature.getGeometry();
                    if (!geometry) return emptyPoint;

                    if (allowedGeometryTypes && allowedGeometryTypes.length > 0) {
                        if (!allowedGeometryTypes.includes(geometry.getType() as any)) return emptyPoint;
                    }

                    if (!featurePassesFilters(feature)) return emptyPoint;

                    return geometryToClusterPoint(geometry);
                };

                const clusterSource = new Cluster<Feature<Geometry>>({
                    source: source,
                    distance: layer.cluster?.distance,
                    geometryFunction: geometryFunction
                })
                clusterSource.set('name', layer.source);
                this.layer[layer.name] = new Vector({
                    name: layer.name,
                    source: clusterSource,
                    shape: layer.shape,
                    visible: layer.visible,
                    base: layer.base,
                    create: layer.create,
                    modify: layer.modify,
                    defaultStyleName: layer.style,
                    style: layer.style ? this.GIS.Style.getStyle(layer.style) : undefined,
                    styleDependency: layer.styleDependency,
                    switcher: layer.switcher,
                    symbology: layer.symbology,
                    zIndex: layer.zIndex,
                    minZoom: layer.minZoom,
                    maxZoom: layer.maxZoom,
                    minResolution: layer.minResolution,
                    maxResolution: layer.maxResolution,
                    declutter: layer.declutter,
                    zoomExtent: layer.zoomExtent
                } as import("ol/layer/Vector.js").Options<Cluster<Feature<Geometry>>> | undefined)
                break;
        }

        if (layer.base) {
            this.basemaps.push(this.layer[layer.name])
        }
    };

    /**
     * TEMPORARY WORKAROUND (task #193100) — emit a single, session-wide console
     * warning the first time any layer configures the mobile tile-performance
     * overrides (`preload` / `cacheSize` / `interpolate`). These overrides
     * mitigate the OpenLayers 9.2.5+/10.x mobile pan/zoom regression and will be
     * removed once the upstream fix ships.
     */
    private warnTemporaryTilePerformanceOverrideOnce(layer: LayerConfiguration) {
        if (this.temporaryTilePerformanceOverrideWarned) {
            return;
        }
        const configuration = layer as { preload?: number; cacheSize?: number; interpolate?: boolean };
        const hasOverride =
            configuration.preload !== undefined ||
            configuration.cacheSize !== undefined ||
            configuration.interpolate !== undefined;
        if (!hasOverride) {
            return;
        }
        this.temporaryTilePerformanceOverrideWarned = true;
        console.warn(
            '[Layer] TEMPORARY WORKAROUND (task #193100): the tile-performance ' +
            'overrides (preload / cacheSize / interpolate) are a temporary mitigation ' +
            'for the OpenLayers 9.2.5+/10.x mobile pan/zoom regression and will be ' +
            'removed once the upstream fix ships.'
        );
    }

    /**
     * Clear the Layer object.
     * Useful, when you need to re-init the map component.
     * Deletes also layers from the Map.
     */
    public clear() {
        this.groupEntries.length = 0;
        if (this.GIS.Map.olObject) {
            this.GIS.Map.olObject!.getLayers().clear();
        }
    }

    private createCQLFilter(layer: WFSLayerConfiguration, extent: Extent) {
        let conditions: string[] = [];

        if (layer.filter?.conditions) {
            conditions.push(...layer.filter.conditions);
        }

        if (layer.filter?.bboxStrategy === 'current') {
            conditions.push(`bbox(${layer.filter.geometry},${extent.join(',')})`);
        } else if (layer.filter?.bboxStrategy === 'max') {
            const viewProps = this.GIS.View.olObject.getProperties();
            const maxExtent = viewProps.extent;
            if (maxExtent) {
                conditions.push(`bbox(${layer.filter.geometry},${maxExtent.join(',')})`);
            }
        }

        return conditions.length > 0 ? `cql_filter=${conditions.join(' AND ')}` : '';
    }

    /**
     * Re-request the failed vector sources, including the ones the map gave up
     * on (`vector_unfetched`), and re-arm the capped automatic sequence.
     * @see {@link fetchFailed} to do the same for raster and vector at once.
     */
    public fetchVectorFailed() {
        // Explicitly requested by the hosting application — re-arm the capped
        // automatic sequence and take back what it gave up on.
        this.retryCycleExhausted = false;
        this.recoverAbandonedVector();
        this.fetchVectorFailedInternal();
    }

    private fetchVectorFailedInternal() {
        for (const property of Object.keys(this.vectorFailed)) {
            const source = this.vectorFailed[property];
            // Optimistically clear: refresh() re-requests the data and the
            // error handlers re-register the failure if it persists.
            delete this.vectorFailed[property];
            source.refresh();
        }
    }

    /**
     * Re-request the failed tiles and images, including the ones the map gave up
     * on (`raster_unfetched`), and re-arm the capped automatic sequence. Tiles
     * that cannot be reloaded on their own (thrown away by OpenLayers) are
     * fetched by refreshing their source.
     * @see {@link fetchFailed} to do the same for raster and vector at once.
     */
    public fetchRasterFailed() {
        this.retryCycleExhausted = false;
        this.recoverAbandonedRaster();
        this.fetchRasterFailedInternal();
    }

    private fetchRasterFailedInternal() {
        // Only layers that could not be re-requested tile by tile. Refreshing a
        // source clears its tile cache, which discards — and therefore cancels —
        // the very requests just re-issued above; those cancellations used to be
        // counted as failures, so every retry pass produced a new batch of
        // "failed" tiles and the watcher never settled.
        const layers = new Set<string>();

        for (const property of Object.keys(this.rasterFailed)) {
            const failed = this.rasterFailed[property];
            const layerName = failed[this.IMAGELAYERNAMEPROPERTY];

            if ((this.rasterRetries[property] || 0) > this.TILERETRIES) {
                this.warnUnfetchable(property, layerName, 'repeatedly failing');
                continue;
            }

            if (this.isDiscardedTarget(failed) || this.isStalledTile(failed)) {
                // Nothing to reload: OpenLayers threw this tile away, or it is
                // stuck in LOADING where load() is a no-op. Only rebuilding the
                // source fetches these again.
                if (layerName) layers.add(layerName);
            } else if (typeof failed.load === 'function') {
                failed.load();
            } else if (failed instanceof HTMLImageElement) {
                failed.src = failed.originalSrc;
            } else {
                console.warn(`Unknown failed type for ${property}:`, failed);
                if (layerName) layers.add(layerName);
            }

            // Optimistically clear the entry: the load above / refresh below
            // re-requests it and the error handlers re-register it if it still
            // fails — so the watcher stops as soon as a retry succeeds.
            delete this.rasterFailed[property];
        }

        layers.forEach(layer => {
            try {
                this.GIS.Layer.getLayer(layer).getSource().refresh();
            } catch (error) {
                console.warn(`[Layer] could not refresh layer '${layer}':`, error);
            }
        });
    }

    /**
     * Refresh the map requests for failed tiles.
     * Can be checked by GIS.Layer.getNumberOfFailed
     *
     * Calling this manually starts a fresh cycle: retry counters are reset so
     * even requests the map gave up on (`raster_unfetched` /
     * `vector_unfetched`) are re-fetched, the capped automatic sequence is
     * re-armed, and the request-failed callback is re-armed too (it will report
     * again if the requests keep failing).
     */
    public fetchFailed() {
        this.rasterRetries = {};
        this.vectorRetries = {};
        this.notifiedFailures.clear();
        this.unfetchableWarned.clear();
        this.retryCycleExhausted = false;
        this.recoverAbandonedRaster();
        this.recoverAbandonedVector();
        this.fetchRasterFailedInternal();
        this.fetchVectorFailedInternal();
    }

    /**
     * Watcher-driven refresh: no counter reset, no callback re-arming and no
     * re-arming of the capped automatic sequence.
     */
    private fetchFailedInternal() {
        this.fetchRasterFailedInternal();
        this.fetchVectorFailedInternal();
    }

    private getRenderPixel(coord: [number, number], map: any, pixelRatio: number): [number, number] {
        const pixel = map.getPixelFromCoordinate(coord);
        return [pixel[0] * pixelRatio, pixel[1] * pixelRatio];
    }

    private attachLoadHandlersToSource(source: Source) {
        const tileErrorHandler = this.handleTileLoadError.bind(this);
        const imageErrorHandler = this.handleImageLoadError.bind(this);
        const tileEndHandler = this.handleTileLoadEnd.bind(this);
        const imageEndHandler = this.handleImageLoadEnd.bind(this);

        if (source instanceof TileSource) {
            source.on('tileloaderror', tileErrorHandler);
            source.on('tileloadend', tileEndHandler);
        }

        if (source instanceof ImageSource) {
            source.on('imageloaderror', imageErrorHandler);
            source.on('imageloadend', imageEndHandler);
        }

        this.tileLoadErrorHandlers.set(source, {tileErrorHandler, imageErrorHandler, tileEndHandler, imageEndHandler});
    }

    private detachErrorHandlersFromSource(source: Source) {
        const handlers = this.tileLoadErrorHandlers.get(source);
        if (!handlers) return;

        if (source instanceof TileSource) {
            if (handlers.tileErrorHandler) source.un('tileloaderror', handlers.tileErrorHandler);
            if (handlers.tileEndHandler) source.un('tileloadend', handlers.tileEndHandler);
        }
        if (source instanceof ImageSource) {
            if (handlers.imageErrorHandler) source.un('imageloaderror', handlers.imageErrorHandler);
            if (handlers.imageEndHandler) source.un('imageloadend', handlers.imageEndHandler);
        }

        this.tileLoadErrorHandlers.delete(source);
    }

    private attachWfsSourceTracking(
        source: VectorSource<Feature<Geometry>>,
        layerName: string
    ) {
        const onLoadEnd = (e: any) => {
            delete this.vectorFailed[layerName];
            delete this.vectorRetries[layerName];
            this.noteRequestFetched(`vector:${layerName}`);
            this.clearNotifiedForLayer(layerName);
            this.resetRetryCycleIfRecovered();

            if (e?.features?.length) {
                e.features.forEach((f: any) => f.set?.(this.IMAGELAYERNAMEPROPERTY, layerName, true));
            }
        };

        const onLoadError = (e: any) => {
            if (!this.vectorFailed[layerName] && !this.vectorAbandoned[layerName]) {
                this.vectorFailed[layerName] = source;
            }

            this.notifyRequestFailed({
                status: 0,
                url: '',
                layerName,
                type: 'vector'
            });

            if (e?.extent && typeof source.removeLoadedExtent === 'function') {
                source.removeLoadedExtent(e.extent as Extent);
            }

            this.armVectorRetry(`vector:${layerName}`, layerName, true, 'failed');
        };

        source.on('featuresloadend', onLoadEnd);
        source.on('featuresloaderror', onLoadError);
    }

    private attachImageSourceTrackingLoader(
        source: XYZ | WMTS | TileWMS | ImageWMS,
        layerName: string
    ) {
        if ('setTileLoadFunction' in source && typeof source.setTileLoadFunction === 'function') {
            source.setTileLoadFunction((tile: Tile, src: string) => {
                const imageTile = tile as ImageTile & { originalSrc?: string; [key: string]: any };
                imageTile.originalSrc = src;
                imageTile[this.IMAGELAYERNAMEPROPERTY] = layerName;

                const imageElement = imageTile.getImage?.() as HTMLImageElement & {
                    originalSrc?: string;
                    [key: string]: any
                };
                if (imageElement) {
                    imageElement.originalSrc = src;
                    imageElement[this.IMAGELAYERNAMEPROPERTY] = layerName;
                }

                fetch(src)
                    .then((response) => {
                        if (!response.ok) {
                            this.notifyRequestFailed({
                                status: response.status,
                                url: src,
                                layerName,
                                type: 'raster'
                            });
                            this.rasterFailed[src] = imageTile;
                            this.armRasterRetry(
                                src, layerName, this.RETRYCODES.includes(response.status), `HTTP ${response.status}`
                            );
                            return Promise.reject(response.status);
                        }
                        delete this.rasterRetries[src];
                        return response.blob();
                    })
                    .then((blob) => {
                        const imageUrl = URL.createObjectURL(blob);
                        if (imageElement) {
                            imageElement.src = imageUrl;
                        }
                        setTimeout(() => URL.revokeObjectURL(imageUrl), this.TILEREVOKE);
                        delete this.rasterFailed[src];
                        delete this.rasterRetries[src];
                        this.noteRequestFetched(src);
                        this.clearNotifiedForLayer(layerName);
                        this.resetRetryCycleIfRecovered();
                    })
                    .catch((reason) => {
                        // A rejection for a tile OpenLayers already threw away
                        // says nothing about the server, so it must not be
                        // collected, notified or retried.
                        if (this.isCanceledRequest(reason, imageTile)) {
                            this.discardCanceledRequest(src, imageTile);
                            return;
                        }
                        if (!this.rasterFailed[src] && !this.rasterAbandoned[src]) {
                            this.rasterFailed[src] = imageTile;
                        }
                        if (typeof reason !== 'number') {
                            // Network error (fetch itself failed) — not an HTTP status
                            this.notifyRequestFailed({
                                status: 0,
                                url: src,
                                layerName,
                                type: 'raster'
                            });
                            this.armRasterRetry(src, layerName, true, 'network-failed');
                        }
                        // Settle the tile so it doesn't block OL's tile queue
                        // and tile.load() can re-trigger the loader on retry.
                        try {
                            imageTile.setState(TileState.ERROR);
                        } catch {
                            // state already settled
                        }
                    });
            });
        } else if ('setImageLoadFunction' in source && typeof source.setImageLoadFunction === 'function') {
            source.setImageLoadFunction((image, src) => {
                const imageElement = image.getImage?.() as HTMLImageElement & {
                    originalSrc?: string;
                    [key: string]: any
                };
                imageElement.originalSrc = src;
                imageElement[this.IMAGELAYERNAMEPROPERTY] = layerName;
                // One image covers the whole layer, so an older failure of it
                // describes a view that is gone (a different extent or size).
                this.dropSupersededImageFailures(layerName, src);

                fetch(src)
                    .then((response) => {
                        if (!response.ok) {
                            this.notifyRequestFailed({
                                status: response.status,
                                url: src,
                                layerName,
                                type: 'raster'
                            });
                            this.rasterFailed[src] = imageElement as any;
                            this.armRasterRetry(
                                src, layerName, this.RETRYCODES.includes(response.status), `HTTP ${response.status}`
                            );
                            return Promise.reject(response.status);
                        }
                        delete this.rasterRetries[src];
                        return response.blob();
                    })
                    .then((blob) => {
                        const imageUrl = URL.createObjectURL(blob);
                        imageElement.src = imageUrl;
                        setTimeout(() => URL.revokeObjectURL(imageUrl), this.TILEREVOKE);
                        delete this.rasterFailed[src];
                        delete this.rasterRetries[src];
                        this.noteRequestFetched(src);
                        this.clearNotifiedForLayer(layerName);
                        this.resetRetryCycleIfRecovered();
                    })
                    .catch((reason) => {
                        if (this.isCanceledRequest(reason, image)) {
                            this.discardCanceledRequest(src, imageElement);
                            return;
                        }
                        if (!this.rasterFailed[src] && !this.rasterAbandoned[src]) {
                            this.rasterFailed[src] = imageElement as any;
                        }
                        if (typeof reason !== 'number') {
                            // Network error (fetch itself failed)
                            this.notifyRequestFailed({
                                status: 0,
                                url: src,
                                layerName,
                                type: 'raster'
                            });
                            this.armRasterRetry(src, layerName, true, 'network-failed');
                        }
                    });
            });
        } else {
            console.warn(`[Layer] attachImageSourceTrackingLoader: Source does not support tile or image load function: ${layerName}`);
        }
    }

    /**
     * True when OpenLayers already threw the tile/image away: it left the
     * viewport, the cache was cleared or the source was refreshed. `dispose()`
     * releases the tile (state `EMPTY`) and drops its image element, so whatever
     * the pending request returns can no longer be rendered — and no
     * `tileloadend` will ever arrive to clear a failure recorded for it.
     */
    private isDiscardedTarget(target: any): boolean {
        if (!target) return true;
        if (target.disposed === true) return true;
        if (typeof target.getState === 'function' && target.getState() === TileState.EMPTY) return true;
        return typeof target.getImage === 'function' && !target.getImage();
    }

    /**
     * Distinguish a meaningless rejection from a genuine failure.
     *
     * A rejection that reaches a tile OpenLayers already threw away is the
     * expected outcome of panning, zooming or refreshing a source — it says
     * nothing about the server. Counting those as failures is what kept
     * {@link getNumberOfFailed} above zero indefinitely and made the retry
     * watcher re-fetch the same tiles forever, because every retry pass
     * discarded the tiles of the sources it refreshed.
     *
     * @param reason - Rejection value: an HTTP status number for a server
     * response, anything else for a fetch-level rejection.
     * @param target - The tile/image the request belongs to, when known.
     */
    private isCanceledRequest(reason: unknown, target?: any): boolean {
        // A status code means the server answered — that is a real failure.
        if (typeof reason === 'number') return false;

        // The tile being gone is what makes the rejection meaningless.
        return target !== undefined && this.isDiscardedTarget(target);
    }

    /**
     * True when a `tileloaderror` / `imageloaderror` describes a cancellation
     * rather than a failed request:
     * - the tile was thrown away by OpenLayers, or
     * - the image element failed on a `blob:` URL, i.e. on data that was
     *   fetched successfully and only failed to decode locally (for instance
     *   because the object URL was already revoked).
     */
    private isCanceledTileError(target: any, src: string): boolean {
        if (typeof src === 'string' && src.startsWith('blob:')) return true;
        return this.isDiscardedTarget(target);
    }

    /**
     * Drop everything recorded for a canceled request. A discarded tile can
     * never report success, so an entry left behind would keep the failure count
     * above zero for good and block the hosting application from printing.
     */
    private discardCanceledRequest(src: string, target?: any) {
        const recorded = this.rasterFailed[src];
        if (recorded && (recorded === target || this.isDiscardedTarget(recorded))) {
            delete this.rasterFailed[src];
            delete this.rasterRetries[src];
        }
        this.resetRetryCycleIfRecovered();
    }

    /**
     * Report a request the map stops re-fetching on its own, once per URL, so a
     * layer that never comes back is visible in the console instead of silently
     * missing from the map.
     */
    private warnUnfetchable(src: string, layerName: string, cause: string) {
        if (!src || this.unfetchableWarned.has(src)) return;
        this.unfetchableWarned.add(src);
        console.warn(
            `[Layer] gave up on ${cause} request for layer '${layerName || 'unknown'}' — ` +
            `not fetched, call fetchFailed() to retry: ${src}`
        );
    }

    /** Forget the "gave up" state of a URL that was fetched successfully. */
    private noteRequestFetched(src?: string) {
        if (src) this.unfetchableWarned.delete(src);
    }

    /**
     * Stop retrying a raster request: move it out of the pending collection into
     * {@link rasterAbandoned}. It is still reported and still re-requested by
     * {@link fetchFailed}, but it no longer keeps {@link getNumberOfFailed}
     * above zero — a request nobody retries any more is not something the
     * hosting application can wait for.
     */
    private retireRasterRequest(src: string) {
        const failed = this.rasterFailed[src];
        if (failed) {
            this.rasterAbandoned[src] = failed;
            delete this.rasterFailed[src];
        }
    }

    private retireVectorRequest(layerName: string) {
        const failed = this.vectorFailed[layerName];
        if (failed) {
            this.vectorAbandoned[layerName] = failed;
            delete this.vectorFailed[layerName];
        }
    }

    /**
     * Give up on everything still pending, so the counter reaches its final
     * value as soon as the sequence ends instead of after another full wait.
     */
    private retireAllPending() {
        Object.keys(this.rasterFailed).forEach((src) => {
            const failed = this.rasterFailed[src];
            this.warnUnfetchable(src, failed?.[this.IMAGELAYERNAMEPROPERTY], 'repeatedly failing');
            this.retireRasterRequest(src);
        });

        Object.keys(this.vectorFailed).forEach((layerName) => {
            this.warnUnfetchable(`vector:${layerName}`, layerName, 'repeatedly failing');
            this.retireVectorRequest(layerName);
        });
    }

    /** Take abandoned requests back into the retry flow. */
    private recoverAbandonedRaster() {
        Object.keys(this.rasterAbandoned).forEach((src) => {
            this.rasterFailed[src] = this.rasterAbandoned[src];
            delete this.rasterAbandoned[src];
        });
    }

    private recoverAbandonedVector() {
        Object.keys(this.vectorAbandoned).forEach((layerName) => {
            this.vectorFailed[layerName] = this.vectorAbandoned[layerName];
            delete this.vectorAbandoned[layerName];
        });
    }

    /**
     * Drop failures nothing can ever clear, so they stop holding the failed
     * counter above zero.
     *
     * Only certain signals are used: OpenLayers released or disposed the tile
     * (it is out of its cache, so it can never report success), or the layer is
     * gone. Relevance must not be guessed from the current view. A source has
     * its own projection and matrix set, so comparing its tile extents with the
     * view extent compares coordinates from different reference systems and
     * never intersects — guessing deleted every failure of such a layer.
     * OpenLayers also draws neighbouring zoom levels as fallbacks and preloads
     * others, so a "wrong zoom" tile can be wanted too.
     *
     * Failures the map has really moved past are handled by the capped sequence
     * retiring them, and by OpenLayers releasing their tiles.
     */
    private pruneIrrelevantFailures() {
        const collections = [this.rasterFailed, this.rasterAbandoned];

        collections.forEach((collection) => {
            Object.keys(collection).forEach((src) => {
                if (this.isIrrelevantFailure(collection[src])) {
                    delete collection[src];
                    delete this.rasterRetries[src];
                    this.unfetchableWarned.delete(src);
                }
            });
        });
    }

    private isIrrelevantFailure(failed: any): boolean {
        if (this.isDiscardedTarget(failed)) return true;

        try {
            const layerName = failed?.[this.IMAGELAYERNAMEPROPERTY];
            return Boolean(layerName) && !this.layer[layerName];     // layer is gone
        } catch {
            // Never let the bookkeeping throw into a render or a public getter.
            return false;
        }
    }

    /**
     * A single-image source has one current request per layer, so an older
     * failure of that layer belongs to a view that no longer exists.
     */
    private dropSupersededImageFailures(layerName: string, src: string) {
        if (!layerName) return;

        [this.rasterFailed, this.rasterAbandoned].forEach((collection) => {
            Object.keys(collection).forEach((key) => {
                if (key !== src && collection[key]?.[this.IMAGELAYERNAMEPROPERTY] === layerName) {
                    delete collection[key];
                    delete this.rasterRetries[key];
                    this.unfetchableWarned.delete(key);
                }
            });
        });
    }

    /**
     * Count a failed raster request and arm the automatic retry sequence.
     *
     * @param retryable - Whether the failure is one the map retries at all
     * (a network error or one of {@link RETRYCODES}; 401/403/404 are not).
     * @param cause - Short description used in the give-up warning.
     * @returns `true` when the request will be re-fetched automatically. When it
     * returns `false` the map has given up on the URL: it is retired out of the
     * pending count and the warning was emitted.
     */
    private armRasterRetry(src: string, layerName: string, retryable: boolean, cause: string): boolean {
        if (cause === 'network-failed' || !this.isOnline()) {
            this.requestStartReconnectProbe();
        }
        if (retryable) {
            // A failure while offline is not an answer from the server: it must
            // not spend the request's retry budget, or panning and zooming
            // offline leaves nothing to retry with — and it must not be given up
            // on, which would mark the whole map as unfetchable.
            if (!this.isOnline()) {
                this.requestStartRetryWatcher();     // resumes once online again
                return true;
            }

            // Keep counting once exhausted so the watcher skips this URL;
            // a manual fetchFailed() resets it and retrieves the request back.
            this.rasterRetries[src] = (this.rasterRetries[src] || 0) + 1;

            if (this.rasterRetries[src] <= this.TILERETRIES && !this.retryCycleExhausted) {
                this.requestStartRetryWatcher();
                return true;
            }
        }

        this.warnUnfetchable(src, layerName, cause);
        this.retireRasterRequest(src);
        return false;
    }

    /** {@link armRasterRetry} for vector sources, which count per layer. */
    private armVectorRetry(url: string, layerName: string, retryable: boolean, cause: string): boolean {
        if (cause === 'network-failed' || !this.isOnline()) {
            this.requestStartReconnectProbe();
        }
        if (retryable) {
            if (!this.isOnline()) {
                this.requestStartRetryWatcher();
                return true;
            }

            this.vectorRetries[layerName] = (this.vectorRetries[layerName] || 0) + 1;

            if (this.vectorRetries[layerName] <= this.TILERETRIES && !this.retryCycleExhausted) {
                this.requestStartRetryWatcher();
                return true;
            }
        }

        this.warnUnfetchable(url, layerName, cause);
        this.retireVectorRequest(layerName);
        return false;
    }

    /**
     * A tile left in `LOADING`: `load()` is a no-op in that state, so only
     * rebuilding the source can fetch it again.
     */
    private isStalledTile(tile: any): boolean {
        return typeof tile?.getState === 'function'
            && tile.getState() === TileState.LOADING;
    }

    /**
     * A failure burst ends once everything collected recovered — only then may
     * a later failure start a fresh automatic retry cycle.
     */
    private resetRetryCycleIfRecovered() {
        if (this.retryCycleExhausted && this.getFailedCount() === 0) {
            this.retryCycleExhausted = false;
        }
    }

    private isOnline(): boolean {
        try {
            return window?.navigator?.onLine !== false;
        } catch {
            return true;
        }
    }

    private waitUntilOnlineThen(cb: () => void) {
        if (this.isOnline()) {
            cb();
            return;
        }

        if (this.onlineOnceHandler) {
            window.removeEventListener('online', this.onlineOnceHandler);
            this.onlineOnceHandler = null;
        }

        this.onlineOnceHandler = () => {
            if (this.onlineOnceHandler) {
                window.removeEventListener('online', this.onlineOnceHandler);
                this.onlineOnceHandler = null;
            }

            this.retryAttempt = 0;

            cb();
        };

        window.addEventListener('online', this.onlineOnceHandler);
    }

    private startRetryWatcher() {
        if (this.retryCycleExhausted) return;
        if (this.retryWatcherTimer !== null || this.startRetryWatcherScheduled) return;
        if (!Object.keys(this.rasterFailed).length && !Object.keys(this.vectorFailed).length) return;

        this.waitUntilOnlineThen(() => {
            if (this.retryCycleExhausted) return;
            if (!Object.keys(this.rasterFailed).length && !Object.keys(this.vectorFailed).length) return;
            this.notePendingSnapshot();     // paces the first attempt
            this.scheduleNextRetry(this.activeRunId);
        });
    }

    private getFailedCount(): number {
        this.pruneIrrelevantFailures();
        return Object.keys(this.rasterFailed).length + Object.keys(this.vectorFailed).length;
    }

    /** Remember how much is pending while the number is still accurate. */
    private notePendingSnapshot() {
        this.pendingSnapshot = this.getFailedCount();
        this.burstPeak = Math.max(this.burstPeak, this.pendingSnapshot);
    }

    /**
     * A burst is converging when the rest of the map made it through and only a
     * residue is still failing — either because requests recovered since the
     * peak, or because it was a handful from the start. Then there is no point
     * in the slow ladder: the sooner the sequence ends, the sooner the hosting
     * application gets a final count.
     */
    private isConvergingBurst(): boolean {
        return this.pendingSnapshot > 0
            && (this.pendingSnapshot <= this.FASTRETRYRESIDUE || this.pendingSnapshot < this.burstPeak);
    }

    /** Delay before the given attempt, shortened while the burst converges. */
    private retryDelayFor(attempt: number): number {
        const configured = this.retryDelays[attempt];
        if (!this.isConvergingBurst()) return configured;

        const accelerated = this.fastRetryDelays[attempt];
        return Number.isFinite(accelerated) ? Math.min(configured, accelerated) : configured;
    }

    private scheduleNextRetry(runId: number) {
        if (runId !== this.activeRunId) return;

        if (this.retryAttempt >= this.retryDelays.length) {
            // Reached only while requests were still failing — a fully recovered
            // burst stops the watcher before this.
            console.warn(
                `[Layer] automatic retries exhausted after ${this.retryDelays.length} attempt(s) — ` +
                `call fetchFailed() to retry. Requests that stay unfetched are reported individually.`
            );
            // Latch the cycle: without this the counter reset in
            // stopRetryWatcher() lets the next failure — including one caused by
            // the retry pass itself — start over at 1/4 and the map re-fetches
            // the same requests indefinitely.
            this.retryCycleExhausted = true;
            // Whatever is still failing is given up on right away, so the count
            // settles now instead of after another wait. The requests stay
            // reported and available to fetchFailed().
            this.retireAllPending();
            this.stopRetryWatcher();    // will also invalidate this runId
            return;
        }

        const delay = this.retryDelayFor(this.retryAttempt);
        if (delay == null || !Number.isFinite(delay)) {
            console.warn('[Layer] invalid retry delay, stopping watcher');
            this.stopRetryWatcher();
            return;
        }
        console.debug(`[Layer] retry ${this.retryAttempt + 1}/${this.retryDelays.length} scheduled in ${delay}ms`);

        this.retryWatcherTimer = window.setTimeout(() => {
            if (runId !== this.activeRunId) return;

            if (this.retryAttempt > this.retryDelays.length) {
                this.stopRetryWatcher();
                return;
            }
            if (!this.isOnline()) {
                console.debug('[Layer] offline — retries paused until online');
                this.stopRetryWatcher();
                this.waitUntilOnlineThen(() => this.requestStartRetryWatcher());
                return;
            }


            // Measure before the pass: it clears the entries optimistically, so
            // afterwards the count says nothing about how much is still failing.
            this.notePendingSnapshot();

            if (this.pendingSnapshot === 0) {
                console.debug('[Layer] all failed requests recovered');
                this.retryCycleExhausted = false;
                this.stopRetryWatcher();
                return;
            }

            console.info(`[Layer] retry ${this.retryAttempt + 1}/${this.retryDelays.length} — ${this.pendingSnapshot} failed request(s)`);
            this.fetchFailedInternal();

            this.retryAttempt++;
            this.scheduleNextRetry(runId); // keep passing the same token
        }, delay);
    }

    private stopRetryWatcher() {
        if (this.retryWatcherTimer !== null) {
            clearTimeout(this.retryWatcherTimer);
            this.retryWatcherTimer = null;
        }

        // A finished cycle (recovered, exhausted or aborted) should not bleed
        // its attempt counter or its pacing into the next failure burst.
        this.retryAttempt = 0;
        this.pendingSnapshot = 0;
        this.burstPeak = 0;
        this.activeRunId = ++this.retryRunId;
    }

    private handleTileLoadError(evt: any) {
        const tile = evt.tile;
        const img = tile?.getImage?.();
        const src = tile?.originalSrc || img?.src;
        if (!src) return;

        // Already recorded by a custom load function (which settles the tile
        // to ERROR state) — don't count or notify the same failure twice.
        if (this.rasterFailed[src]) return;

        if (this.isCanceledTileError(tile, src)) return;

        const layerName = tile?.[this.IMAGELAYERNAMEPROPERTY] || '';

        this.rasterFailed[src] = tile;
        this.notifyRequestFailed({
            status: 0,
            url: src,
            layerName,
            type: 'raster'
        });
        this.armRasterRetry(src, layerName, true, 'failed');
    }

    private handleImageLoadError(evt: any) {
        const img = evt.image?.getImage?.() as (HTMLImageElement & { originalSrc?: string; [key: string]: any });
        const src = img?.originalSrc || img?.src;
        if (!src) return;

        if (this.isCanceledTileError(img, src) || this.isCanceledTileError(evt.image, src)) return;

        const layerName = img?.[this.IMAGELAYERNAMEPROPERTY] || '';

        this.rasterFailed[src] = img as any;
        this.notifyRequestFailed({
            status: 0,
            url: src,
            layerName,
            type: 'raster'
        });
        this.armRasterRetry(src, layerName, true, 'failed');
    }

    private handleTileLoadEnd(evt: any) {
        const tile = evt.tile;
        const state = tile.getState?.();
        const img = tile?.getImage?.();
        const originalSrc = tile?.originalSrc;
        const currentSrc = img?.src;

        if (state !== TileState.LOADED) {
            return;
        }

        [originalSrc, currentSrc].forEach((k) => {
            if (!k) return;
            delete this.rasterFailed[k];
            delete this.rasterRetries[k];
            this.noteRequestFetched(k);
        });
        this.clearNotifiedForLayer(tile?.[this.IMAGELAYERNAMEPROPERTY] || '');
        this.resetRetryCycleIfRecovered();
    }

    private handleImageLoadEnd(evt: any) {
        const img = evt.image?.getImage?.() as (HTMLImageElement & { originalSrc?: string; [key: string]: any });
        const originalSrc = img?.originalSrc;
        const currentSrc = img?.src;

        [originalSrc, currentSrc].forEach((k) => {
            if (!k) return;
            delete this.rasterFailed[k];
            delete this.rasterRetries[k];
            this.noteRequestFetched(k);
        });
        this.clearNotifiedForLayer(img?.[this.IMAGELAYERNAMEPROPERTY] || '');
        this.resetRetryCycleIfRecovered();
    }

    /**
     * The connection came back: rebuild every layer that failed while it was
     * gone, and forget the bookkeeping of those failures — they were about
     * connectivity, not about the servers.
     *
     * Rebuilding the source is what makes this reliable. Re-requesting tile by
     * tile only works for tiles that are still live, and after panning and
     * zooming offline most are not: their objects are stale, so nothing came
     * back and the map kept its blanks. `refresh()` clears the tile cache, so
     * every tile the view needs is requested again — and it happens once per
     * reconnect, not on every retry pass.
     */
    private recoverAfterReconnect() {
        this.stopReconnectProbe();

        const rasterLayers = new Set<string>();
        [this.rasterFailed, this.rasterAbandoned].forEach((collection) => {
            Object.keys(collection).forEach((src) => {
                const layerName = collection[src]?.[this.IMAGELAYERNAMEPROPERTY];
                if (layerName) rasterLayers.add(layerName);
            });
        });

        const vectorLayers = new Set([
            ...Object.keys(this.vectorFailed),
            ...Object.keys(this.vectorAbandoned)
        ]);

        if (!rasterLayers.size && !vectorLayers.size) return;

        const rebuilt = [...rasterLayers, ...vectorLayers];
        const unattributed = Object.keys(this.rasterFailed)
            .filter((src) => !this.rasterFailed[src]?.[this.IMAGELAYERNAMEPROPERTY]).length;

        console.debug(
            `[Layer] back online — rebuilding ${rebuilt.length} layer(s) that failed while offline: ` +
            `${rebuilt.join(', ')}` + (unattributed ? ` (${unattributed} request(s) without a layer name)` : '')
        );

        // Clean slate: the rebuild re-requests everything, and the error
        // handlers re-register whatever genuinely fails.
        this.rasterFailed = {};
        this.rasterAbandoned = {};
        this.vectorFailed = {};
        this.vectorAbandoned = {};
        this.rasterRetries = {};
        this.vectorRetries = {};
        this.notifiedFailures.clear();
        this.unfetchableWarned.clear();
        this.retryCycleExhausted = false;
        this.stopRetryWatcher();

        [...rasterLayers, ...vectorLayers].forEach((layerName) => {
            try {
                // Resolved through the internal map: getLayer() throws for a
                // layer that was removed in the meantime.
                this.layer[layerName]?.getSource()?.refresh();
            } catch (error) {
                console.warn(`[Layer] could not rebuild layer '${layerName}' after reconnecting:`, error);
            }
        });
    }

    /**
     * Start probing for the connection coming back. Armed by failures that look
     * like a lost connection (a network-level failure, or any failure while the
     * browser reports itself offline), because the `online` event alone is not
     * enough: it only fires when the network interface changes, never when a
     * VPN, a proxy or the route to the server drops.
     */
    private requestStartReconnectProbe() {
        this.reconnectRecoveryArmed = true;
        if (this.reconnectProbeTimer !== null) return;
        this.reconnectProbeTimer = window.setInterval(() => this.probeReconnect(), this.RECONNECTPROBEINTERVAL);
    }

    private stopReconnectProbe() {
        if (this.reconnectProbeTimer !== null) {
            clearInterval(this.reconnectProbeTimer);
            this.reconnectProbeTimer = null;
        }
        this.reconnectRecoveryArmed = false;
    }

    /** One of the failed URLs, used to test whether the connection is back. */
    private reconnectProbeUrl(): string | undefined {
        return [...Object.keys(this.rasterFailed), ...Object.keys(this.rasterAbandoned)]
            .find((source) => typeof source === 'string' && !source.startsWith('blob:'));
    }

    private probeReconnect() {
        if (!this.reconnectRecoveryArmed) {
            this.stopReconnectProbe();
            return;
        }

        const url = this.reconnectProbeUrl();
        if (!url) {
            // Nothing left to recover (or only vector sources, which the retry
            // watcher re-requests on its own once online).
            this.stopReconnectProbe();
            return;
        }

        // The probe carries no Authorization header — the credentials live in
        // the loaders installed by setLayerLoader, out of the probe's reach. So
        // ANY response, a 401 from a Basic/Bearer-protected layer included,
        // proves the connection is back; the rebuild then re-requests through
        // the sources' own loaders, which do send the credentials.
        fetch(url, {cache: 'no-store'})
            .then(() => {
                console.debug('[Layer] reconnect probe was answered — the connection is back');
                this.recoverAfterReconnect();
            })
            .catch(() => {
                // Still unreachable — keep probing.
            });
    }

    private attachOnlineOfflineListeners() {
        if (this.listenersAttached) return;

        this.onlineHandler = () => {
            this.recoverAfterReconnect();
        };

        this.offlineHandler = () => {
            this.stopRetryWatcher();
            if (this.onlineOnceHandler) {
                window.removeEventListener('online', this.onlineOnceHandler);
                this.onlineOnceHandler = null;
            }
        };

        window.addEventListener('online', this.onlineHandler);
        window.addEventListener('offline', this.offlineHandler);
        this.listenersAttached = true;
    }

    private requestStartRetryWatcher() {
        // The automatic sequence is capped per failure burst — a new cycle only
        // starts once everything recovered or fetchFailed() is called.
        if (this.retryCycleExhausted) return;
        if (this.retryWatcherTimer !== null || this.startRetryWatcherScheduled) return;
        if (!Object.keys(this.rasterFailed).length && !Object.keys(this.vectorFailed).length) return; // guard
        this.startRetryWatcherScheduled = true;
        setTimeout(() => {
            this.startRetryWatcherScheduled = false;
            this.startRetryWatcher();
        }, 0);
    }

    /**
     * Update raster layer params.
     * @example GIS.Layer.updateParams('layerName', {viewparams: 'sessionId:someSessionId'})
     * @param layername Layer name
     * @param params Parameters as an object of keys, used for updating params
     * @throws No such RASTER layer in the map.
     */
    public updateParams(layername: string, params: any) {
        if (!this.layer[layername]) {
            throw new Error(`No such layer '${layername}' in the map.`);
        }

        const source = this.layer[layername].getSource();

        if (source instanceof WMTS) {
            if (source.getRequestEncoding() === 'REST') {
                const urls = source.getUrls();
                if (urls) {
                    const updatedUrls = urls.map(url => {
                        let updatedUrl = url;
                        Object.keys(params).forEach(key => {
                            const regex = new RegExp(`{${key}}`, 'g');
                            if (regex.test(updatedUrl)) {
                                updatedUrl = updatedUrl.replace(regex, params[key]);
                            } else {
                                const separator = updatedUrl.includes('?') ? '&' : '?';
                                updatedUrl += `${separator}${key}=${params[key]}`;
                            }
                        });
                        return updatedUrl;
                    });
                    source.setUrls(updatedUrls);
                } else {
                    console.warn(`No URLs found for layer: '${layername}'`);
                }
            } else if (source.getRequestEncoding() === 'KVP') {
                const existingDimensions = source.getDimensions() || {};
                const updatedDimensions = {...existingDimensions, ...params};
                source.updateDimensions(updatedDimensions);
            }
        } else {
            const layerParams = source.getParams?.();
            if (layerParams) {
                Object.keys(params).forEach(key => {
                    layerParams[key] = params[key];
                });
                source.updateParams(layerParams);
            } else {
                console.warn(`Could not update params for layer: '${layername}'. The source does not support parameters.`);
            }
        }

        this.clearFailuresByLayer(layername);
    }

    private createRasterLoadFunction(auth: string, layerName: string, scheme: AuthenticationScheme = this.DEFAULTAUTHENTICATIONSCHEME) {
        return (tile: ImageTile & { [key: string]: any }, src: string) => {
            const image = tile.getImage() as HTMLImageElement;
            tile[this.IMAGELAYERNAMEPROPERTY] = layerName;
            if (!tile.tileCoord) {
                // Installed on a single-image source (no tile coordinate), where
                // an older failure of this layer belongs to a view that is gone.
                this.dropSupersededImageFailures(layerName, src);
            }
            let options: FetchOptions = {};
            if (auth) {
                options['headers'] = new Headers({'Authorization': (scheme || this.DEFAULTAUTHENTICATIONSCHEME) + ' ' + auth});
            }
            fetch(src, options)
                .then((response) => {
                    if (!response.ok) {
                        this.notifyRequestFailed({
                            status: response.status,
                            url: src,
                            layerName,
                            type: 'raster'
                        });
                        // Always collect the failed tile so a manual fetchFailed()
                        // (e.g. after a token refresh) can retrieve it back.
                        this.rasterFailed[src] = tile;
                        // Non-retryable statuses (401/403/…) are collected but not auto-retried.
                        this.armRasterRetry(
                            src, layerName, this.RETRYCODES.includes(Number(response.status)), `HTTP ${response.status}`
                        );
                        return Promise.reject(response.status);
                    }
                    delete this.rasterRetries[src];
                    return response.blob();
                })
                .then((blob) => {
                    const imageUrl = URL.createObjectURL(blob);
                    if (image) {
                        image.src = imageUrl;
                    }
                    setTimeout(() => URL.revokeObjectURL(imageUrl), this.TILEREVOKE);
                    delete this.rasterFailed[src];
                    delete this.rasterRetries[src];
                    this.noteRequestFetched(src);
                    this.clearNotifiedForLayer(layerName);
                    this.resetRetryCycleIfRecovered();
                })
                .catch((reason) => {
                    // A rejection for a tile OpenLayers already threw away says
                    // nothing about the server — drop the bookkeeping instead of
                    // counting a failure nothing could ever clear.
                    if (this.isCanceledRequest(reason, tile)) {
                        this.discardCanceledRequest(src, tile);
                        return;
                    }
                    if (!this.rasterFailed[src] && !this.rasterAbandoned[src]) {
                        this.rasterFailed[src] = tile;
                    }
                    if (typeof reason !== 'number') {
                        // Network error (fetch itself failed) — not an HTTP status
                        this.notifyRequestFailed({
                            status: 0,
                            url: src,
                            layerName,
                            type: 'raster'
                        });
                        this.armRasterRetry(src, layerName, true, 'network-failed');
                    }
                    tile.setState(TileState.ERROR);
                })
        }
    }

    private createVectorLoadFunction(layer: LayerType, source: VectorSource, auth: string, layerName: string, scheme: AuthenticationScheme = this.DEFAULTAUTHENTICATIONSCHEME) {
        return (extent: Extent) => {
            let options: FetchOptions = {};
            if (auth) {
                options['headers'] = new Headers({'Authorization': (scheme || this.DEFAULTAUTHENTICATIONSCHEME) + ' ' + auth});
            }
            const requestUrl = layer.get('fullPath');
            fetch(requestUrl, options)
                .then((response) => {
                    if (!response.ok) {
                        this.notifyRequestFailed({
                            status: response.status,
                            url: requestUrl,
                            layerName,
                            type: 'vector'
                        });
                        // Always collect the failed source for manual fetchFailed().
                        this.vectorFailed[layerName] = source;
                        this.armVectorRetry(
                            requestUrl, layerName, this.RETRYCODES.includes(Number(response.status)), `HTTP ${response.status}`
                        );
                        return Promise.reject(response.status);
                    }
                    delete this.vectorRetries[layerName];
                    return response.text();
                })
                .then((response) => {
                    const format = source.getFormat();
                    if (format) {
                        const features = format.readFeatures(response) as Feature<Geometry>[];
                        source.addFeatures(features);
                        delete this.vectorFailed[layerName];
                        delete this.vectorRetries[layerName];
                        this.noteRequestFetched(requestUrl);
                        this.clearNotifiedForLayer(layerName);
                        this.resetRetryCycleIfRecovered();
                    }
                })
                .catch((reason) => {
                    if (!this.vectorFailed[layerName] && !this.vectorAbandoned[layerName]) {
                        this.vectorFailed[layerName] = source;
                    }
                    if (typeof reason !== 'number') {
                        // Network error (fetch itself failed) — not an HTTP status
                        this.notifyRequestFailed({
                            status: 0,
                            url: requestUrl,
                            layerName,
                            type: 'vector'
                        });
                        this.armVectorRetry(requestUrl, layerName, true, 'network-failed');
                    }
                    source.removeLoadedExtent(extent);
                })
        }
    }

    private setStyleDependency(config: LayerConfiguration) {
        const layer = this.getLayer(config.name);
        if (layer.get('styleDependency')) {
            switch (config.type) {
                case this.type.WMSTILED:
                case this.type.WMS:
                    layer.get('styleDependency').map((dependency: StyleDependency) => {
                        this.getLayer(dependency.layer).on('change:visible', (event: ObjectEvent) => {
                            const source = layer.getSource();
                            if (source) {
                                const params = source.getParams();
                                if (!event.oldValue) {
                                    params.STYLES = dependency.style;
                                } else {
                                    params.STYLES = config.styles;
                                }
                                source.updateParams(params);
                            }
                        })
                    });
                    break;
                case this.type.GEOLOCATION:
                case this.type.WEBGLPOINTS:
                case this.type.GEOJSON:
                case this.type.WFS:
                case this.type.CLUSTER:
                    layer.get('styleDependency').map((dependency: StyleDependency) => {
                        this.getLayer(dependency.layer).on('change:visible', event => {
                            if (!event.oldValue && layer instanceof BaseVectorLayer) {
                                const olStyle = this.GIS.Style.getStyle(dependency.style) as StyleLike | undefined;
                                layer.setStyle(olStyle);
                            } else if (layer instanceof BaseVectorLayer && config.style) {
                                const olStyle = this.GIS.Style.getStyle(config.style) as StyleLike | undefined;
                                layer.setStyle(olStyle);
                            }
                        })
                    });
                    break;

            }
        }
    }

    private setStyleDependencies(configs: LayerConfiguration[]) {
        configs.map(config => {
            this.setStyleDependency(config);
        })
    }

    /**
     * Add layer configuration to group
     *
     * @param config Layer config, be aware that a style should be defined
     * @param groupName Name of the group.
     */
    public addLayer(config: LayerConfiguration, groupName: string) {
        this.create(config);
        this.setStyleDependency(config);
        this.addLayerToGroup(config.name, groupName);
        if (config.switcher) {
            const layersControl = this.GIS.Control.getControl('layers') as unknown as LayersControl;
            if (layersControl && typeof layersControl.addLayerToSwitcher === "function") {
                layersControl.addLayerToSwitcher(groupName, config);
            }
        }
    }

    /**
     * Remove one layer from the group.
     *
     * @param name Name of the layer.
     * @param groupName Name of the group.
     */
    public removeLayer(name: string, groupName: string) {
        const olGroup = this.group[groupName];

        const layers = olGroup.getLayers();

        const layerToRemove = this.getLayer(name) as LayerType & SwitcherActivation;

        if (layerToRemove) {
            if (this.layer[name]) {
                if (layerToRemove.activateSwitcher) {
                    const layersControl = this.GIS.Control.getControl('layers') as unknown as LayersControl;
                    layersControl.removeLayerFromSwitcher(name, this.GIS.getInstanceCounter());
                }
                delete this.layer[name];
            } else {
                throw new Error('No such layer in the map.');
            }

            layers.remove(layerToRemove);
        } else {
            throw new Error('No such layer in the OlGroup.');
        }
    }

    private initGroup(config: GroupConfiguration[]) {
        config.map(entry => {
            if (!this.group[entry.name]) {
                this.createGroup(entry);
            }
            const olLayersCollection: Collection<BaseLayer> = new Collection();
            entry.layers?.map(layer => {
                olLayersCollection.push(this.getLayer(layer));
            });
            this.group[entry.name].setLayers(olLayersCollection);
            this.groupEntries.push(this.group[entry.name]);
        });
    }

    /**
     * @param name of a group
     * @returns {Group} return Ol group
     */
    public getGroup(name: string) {
        if (this.group[name]) {
            return this.group[name];
        } else {
            throw new Error('No such group in the map.');
        }
    }

    /**
     * Get group extent
     * @param name Group name
     * @param callback
     * @example Layer.getGroupExtent('position', (minx, miny, maxx, maxy) => {console.log(minx)})
     * @throws No such group in the map.
     * @throws Empty extent.
     * @returns Extent array [minx, miny, maxx, maxy]
     */
    public getGroupExtent(name: string, callback?: GetGroupExtent) {
        const groupExtent = createEmpty();
        const olGroup = this.getGroup(name);

        const layers = olGroup.getLayers() as Collection<LayerType>;
        layers.forEach((layer: LayerType) => {
            const source = layer.getSource();
            if (source && source.getExtent) {
                const extent = source.getExtent();
                if (!isEmpty(extent)) {
                    extend(groupExtent, extent);
                }
            }
        });

        if (isEmpty(groupExtent)) {
            throw new Error('Empty extent.');
        }

        if (callback) {
            callback(groupExtent[0], groupExtent[1], groupExtent[2], groupExtent[3]);
        }

        return groupExtent;
    }

    public addLayerToGroup(layerName: string, groupName: string) {
        let olGroup = this.getGroup(groupName);
        if (!olGroup) {
            this.createGroup({name: groupName, title: groupName});
            olGroup = this.getGroup(groupName);
        }

        const layers = olGroup.getLayers();
        const olLayer = this.getLayer(layerName);

        layers.push(olLayer);
    }

    public clearGroup(groupName: string) {
        const olGroup = this.getGroup(groupName);
        let layerArray, len;
        layerArray = olGroup.getLayers().getArray();
        len = layerArray.length;
        while (len > 0) {
            layerArray.pop();
            len = layerArray.length;
        }
        olGroup.changed();
    }

    public createGroup(config: GroupConfiguration) {
        this.group[config.name] = new Group({
            name: config.name,
            title: config.title,
            collapsible: config.collapsible,
            zIndex: config.zIndex,
            groupLayer: config.groupLayer,
            allLayersVisibilityCheck: config.allLayersVisibilityCheck,
            filter: config.filter,
            i18n: config.i18n
        } as olGroupOptions | undefined);
    }

    /**
     * Set group visibility
     * @param name Group names in
     * @param visible true/false
     * @example Layer.setGroupVisibility('operative', true);
     * @throws No such group in the map.
     */
    public setGroupVisibility(name: string, visible: boolean) {
        const olGroup = this.getGroup(name);

        const layers = olGroup.getLayers();
        layers.forEach(layer => {
            this.setLayerVisibility(layer.get('name'), visible);
        });
    }

    /**
     * Create a `wmsTiledGroup` layer: one physical grid-aligned TileWMS layer
     * that lets the server composite several WMS layers into a single tile
     * pyramid, plus one source-less virtual layer per sub-layer so each
     * sub-layer can be toggled in the layer switcher like an ordinary layer.
     *
     * Toggling a sub-layer rebuilds the combined `LAYERS` parameter in the
     * canonical order given by `subLayers` and reloads the physical layer.
     * While no sub-layer is visible the physical layer is hidden entirely,
     * so no request with an empty `LAYERS` parameter is ever issued.
     */
    private createWmsTiledGroupLayer(layer: WMSTiledGroupLayerConfiguration) {
        const initialVisibleSubLayers = layer.subLayers.filter(sub => sub.visible);
        const initialServerLayers = initialVisibleSubLayers.map(sub => sub.layername ?? sub.name);

        const wmsParams: WMSPARAMS = {
            'LAYERS': initialServerLayers,
            'TILED': layer.tiled ?? true,
            'FORMAT': layer.format,
            'VERSION': layer.version,
            'STYLES': layer.styles ? layer.styles : "",
            'ENV': layer.env ? layer.env : "",
            'TOKEN': layer.token
        };

        const initialOpacities = this.buildWmsTiledGroupOpacities(initialVisibleSubLayers);
        if (initialOpacities !== undefined) {
            wmsParams['OPACITIES'] = initialOpacities;
        }

        if (parseFloat(layer.version) >= 1.3 && layer.epsg) {
            wmsParams['SRS'] = "EPSG:" + String(layer.epsg);
        }

        this.layer[layer.name] = new TileLayer({
            className: layer.type + '-' + layer.name,
            name: layer.name,
            visible: layer.visible !== false && initialServerLayers.length > 0,
            base: layer.base,
            opacity: layer.opacity,
            preload: layer.preload ?? 0,
            cacheSize: layer.cacheSize,
            source: new TileWMS({
                url: layer.url,
                params: wmsParams,
                projection: this.GIS.Projection.getRegistered(layer.epsg),
                serverType: layer.serverType,
                tileGrid: this.GIS.Projection.grid[layer.matrixSet],
                transition: layer.transition,
                interpolate: layer.interpolate,
                crossOrigin: "anonymous"
            }),
            zIndex: layer.zIndex,
            minZoom: layer.minZoom,
            maxZoom: layer.maxZoom,
            minResolution: layer.minResolution,
            maxResolution: layer.maxResolution,
            zoomExtent: layer.zoomExtent
        } as import("ol/layer/BaseTile").Options<TileSource> | undefined);

        if (layer.authentication) {
            this.setLayerLoader(layer.name, layer.authentication, {scheme: layer.authenticationScheme});
        } else {
            this.attachImageSourceTrackingLoader(this.layer[layer.name].getSource(), layer.name);
            this.attachLoadHandlersToSource(this.layer[layer.name].getSource());
        }

        layer.subLayers.forEach(sub => {
            const virtual = new TileLayer({
                className: 'wmsTiledGroupEntry-' + sub.name,
                name: sub.name,
                // Marks the layer as a virtual sub-layer of a server-side
                // composite so the switcher hides its opacity slider: a live
                // per-band slider is impossible while the layer renders through
                // the combined group request (fixed opacity is baked in via the
                // OPACITIES param instead).
                wmsTiledGroupEntry: true,
                visible: sub.visible,
                opacity: sub.opacity,
                minZoom: sub.minZoom,
                maxZoom: sub.maxZoom,
                switcher: sub.switcher,
                symbology: sub.symbology,
                zoomExtent: sub.zoomExtent
            } as import("ol/layer/BaseTile").Options<TileSource> | undefined);
            virtual.on('change:visible', () => this.refreshWmsTiledGroupLayer(layer.name, layer.subLayers));
            // Offline support: when the Offline module swaps a local tile
            // source onto the virtual layer it starts rendering on its own,
            // so the group request must exclude it (and re-include it after
            // the source is rolled back to null).
            virtual.on('change:source', () => this.refreshWmsTiledGroupLayer(layer.name, layer.subLayers));
            this.layer[sub.name] = virtual;
        });
    }

    /**
     * Rebuild the combined `LAYERS` parameter of a `wmsTiledGroup` layer from
     * the visibility of its virtual sub-layers. The parameter is always built
     * in `subLayers` order, so identical layer combinations yield identical
     * request URLs regardless of toggle order (browser-cacheable).
     *
     * Sub-layers that currently carry their own source (offline tile bundles
     * activated through the Offline module) are excluded — they render
     * themselves. When every visible sub-layer is offline-sourced or hidden,
     * the physical layer is hidden entirely and issues no online requests.
     */
    private refreshWmsTiledGroupLayer(layerName: string, subLayers: WMSTiledGroupSubLayerConfiguration[]) {
        const physical = this.layer[layerName];
        if (!physical) {
            return;
        }

        const activeSubLayers = subLayers.filter(sub => {
            const virtual = this.layer[sub.name];
            // A sub-layer with its own source (offline tiles swapped in by
            // the Offline module) renders itself and must not be part of
            // the combined request.
            return virtual?.getVisible() && !virtual.getSource();
        });
        const serverLayers = activeSubLayers.map(sub => sub.layername ?? sub.name);

        if (serverLayers.length === 0) {
            physical.setVisible(false);
            return;
        }

        const source = physical.getSource();
        if (source instanceof TileWMS) {
            const params = source.getParams();
            const current = params['LAYERS'];
            const currentKey = Array.isArray(current) ? current.join(',') : String(current ?? '');
            const nextKey = serverLayers.join(',');

            // OPACITIES must stay aligned to the (filtered) LAYERS order, so it
            // is rebuilt from the same active sub-layer set on every change.
            const currentOpacities = params['OPACITIES'] ?? '';
            const nextOpacities = this.buildWmsTiledGroupOpacities(activeSubLayers);

            if (currentKey !== nextKey || currentOpacities !== (nextOpacities ?? '')) {
                if (nextOpacities === undefined) {
                    // Every active band is fully opaque — drop the param so the
                    // URL matches the pre-opacity form and stays cacheable.
                    delete params['OPACITIES'];
                    source.updateParams({'LAYERS': serverLayers});
                } else {
                    source.updateParams({'LAYERS': serverLayers, 'OPACITIES': nextOpacities});
                }
            }
        }
        physical.setVisible(true);
    }

    /**
     * Build the GeoServer `OPACITIES` vendor parameter for a `wmsTiledGroup`
     * from a set of sub-layers, in the same order they appear in the combined
     * `LAYERS` parameter. Each sub-layer's `0`–`1` opacity (default `1`) maps
     * to GeoServer's `0`–`255` scale.
     *
     * Returns `undefined` when every sub-layer is fully opaque, so the common
     * case keeps the request URL free of the parameter — shorter, and
     * byte-identical to the pre-opacity URLs, avoiding needless cache churn.
     */
    private buildWmsTiledGroupOpacities(subLayers: WMSTiledGroupSubLayerConfiguration[]): string | undefined {
        const hasTransparentBand = subLayers.some(sub => sub.opacity !== undefined && sub.opacity < 1);
        if (!hasTransparentBand) {
            return undefined;
        }
        return subLayers
            .map(sub => {
                const opacity = Math.min(Math.max(sub.opacity ?? 1, 0), 1);
                return String(Math.round(opacity * 255));
            })
            .join(',');
    }

    createRasterSource(config: LayerConfiguration): XYZ {
        if (config.type === this.type.XYZ) {
            return new XYZ({
                url: config.url,
                projection: this.GIS.Projection.getRegistered(config.epsg),
                maxZoom: config.maxZoom,
                tileGrid: this.GIS.Projection.grid[config.matrixSet],
                maxResolution: config.maxResolution,
                interpolate: config.interpolate,
                wrapX: config.wrapX,
            });
        }
        throw new Error(`Unsupported layer type: ${config.type}`);
    }

    /**
     * @param name of a layer
     * @return {LayerType} The layer object from the map
     */
    getLayer(name: string) {
        if (this.layer[name]) {
            /* return Ol Layer */
            return this.layer[name];
        } else {
            throw new Error('No such layer in the map.');
        }
    }

    /**
     * Build a standard WMS GetFeatureInfo URL for a clicked coordinate. The
     * request structure (BBOX, WIDTH, HEIGHT, I, J — or X/Y for WMS 1.1.1)
     * matches the requests a desktop GIS such as QGIS generates: the clicked
     * coordinate, the current view resolution/extent and projection are passed
     * automatically.
     *
     * By default the BBOX is derived from the resolution of the **current zoom
     * level**: it spans a small window of current-zoom pixels centred on the
     * click (a few metres when zoomed in), so the server evaluates the map pixel
     * that was actually clicked. Pass `options.searchSize` to query a fixed
     * ground size instead — e.g. `2` for a 2x2 m box at every zoom level.
     *
     * `wms` (ImageWMS) and `wmsTiled` (TileWMS) layers keep their own WMS
     * endpoint, CRS and parameters (LAYERS, STYLES, VERSION, ENV, …) — they are
     * read from the layer's source, so the query matches what is drawn on the
     * map. OpenLayers' own request builder is deliberately **not** used: it
     * always samples a fixed 101x101 pixel window, which spans several
     * kilometres at mid zoom levels and made GetFeatureInfo report features far
     * away from the click. For layers whose OpenLayers source is not a WMS
     * source — e.g. a layer configured as `wmts` whose server also exposes a
     * WMS GetFeatureInfo endpoint — supply the WMS endpoint via `options.url`
     * (and optionally `layers` / `version` / `format`) when it cannot be
     * derived from the source.
     *
     * @param layerName - Name of the layer as registered in the map.
     * @param coordinate - Clicked coordinate in the view projection.
     * @param options - {@link FeatureInfoOptions} (INFO_FORMAT, FEATURE_COUNT,
     *   click tolerance, url/layers/version/format overrides, etc).
     * @returns The GetFeatureInfo URL, or `undefined` when one cannot be built.
     * @throws No such layer in the map.
     * @throws Cannot build a GetFeatureInfo URL ... (no WMS endpoint available).
     */
    public getFeatureInfoUrl(layerName: string, coordinate: Coordinate, options?: FeatureInfoOptions): string | undefined {
        const source = this.getLayer(layerName).getSource();

        return this.buildFeatureInfoUrl(layerName, source, coordinate, options);
    }

    /**
     * Normalise a WMS LAYERS / QUERY_LAYERS value to a comma-separated string.
     * Accepts a single layer name or an array of names; returns `undefined`
     * when the value itself is `undefined`.
     */
    private joinLayers(value: string | string[] | undefined): string | undefined {
        if (value === undefined) {
            return undefined;
        }
        return Array.isArray(value) ? value.join(',') : value;
    }

    /**
     * WMS parameters the layer's own source sends with its GetMap requests
     * (LAYERS, STYLES, VERSION, FORMAT, ENV, …). They seed the GetFeatureInfo
     * request so the query matches what the map draws. Empty for sources that
     * are not WMS sources.
     */
    private featureInfoSourceParams(source: Source): { [key: string]: unknown } {
        if (source instanceof TileWMS || source instanceof ImageWMS) {
            return source.getParams() ?? {};
        }
        return {};
    }

    /**
     * A WMS parameter value as a string, or `undefined` when it carries nothing
     * (so `??` chains fall through to the next default).
     */
    private stringParam(value: unknown): string | undefined {
        if (value === undefined || value === null) {
            return undefined;
        }
        const param = Array.isArray(value) ? value.join(',') : String(value);
        return param === '' ? undefined : param;
    }

    /** Request parameters a GetFeatureInfo URL always defines itself, so they are
     * never inherited from the layer source or the endpoint URL. */
    private readonly FEATUREINFOPARAMS = ['REQUEST', 'SERVICE', 'VERSION', 'LAYERS', 'QUERY_LAYERS',
        'STYLES', 'FORMAT', 'INFO_FORMAT', 'WIDTH', 'HEIGHT', 'BBOX', 'CRS', 'I', 'J', 'X', 'Y',
        'FEATURE_COUNT', 'BUFFER'];

    /**
     * Normalise a source's WMS parameters into request key/value strings,
     * dropping the ones a GetFeatureInfo request defines itself.
     */
    private wmsParams(sourceParams: { [key: string]: unknown }): { [key: string]: string } {
        const params: { [key: string]: string } = {};
        Object.entries(sourceParams).forEach(([key, value]) => {
            const name = key.toUpperCase();
            const param = this.stringParam(value);
            if (param !== undefined && !this.FEATUREINFOPARAMS.includes(name)) {
                params[name] = param;
            }
        });
        return params;
    }

    /**
     * Parameters already present in the endpoint's query string — an access
     * token, a gateway key and the like — which are kept in the GetFeatureInfo
     * URL. Anything the request defines itself is dropped, so e.g. the
     * `SERVICE=WMTS&REQUEST=GetTile` of a tile URL does not leak through.
     */
    private endpointParams(query?: string): { [key: string]: string } {
        const params: { [key: string]: string } = {};
        if (!query) {
            return params;
        }
        new URLSearchParams(query).forEach((value, key) => {
            if (!this.FEATUREINFOPARAMS.includes(key.toUpperCase())) {
                params[key] = value;
            }
        });
        return params;
    }

    /**
     * Radius, in pixels of the **current zoom level**, sampled around the click
     * when the caller gives no tolerance. The sampling window is
     * `2 * radius + 1` pixels across and the BBOX spans exactly that many
     * current-resolution pixels, so a click at a high zoom level asks about a
     * few metres of ground while the same click zoomed out scales up with the
     * resolution. A large fixed window (such as OpenLayers' internal 101x101
     * one) would instead span several kilometres at mid zoom levels, which is
     * what made GetFeatureInfo return features far away from the click.
     */
    private readonly FEATUREINFORADIUS = 1;

    /** Smallest sampling window sent, in pixels. A 1x1 request is legal WMS but
     * fragile in practice: servers derive the request's scale denominator from
     * `BBOX / WIDTH`, so a single pixel declares a scale far coarser than the map
     * and scale-dependent SLD rules can stop reporting the feature, and mapping
     * `I` back to a world coordinate degenerates when `WIDTH` is 1. */
    private readonly FEATUREINFOMINWINDOW = 3;

    /** Largest sampling window sent, in pixels. Caps the image the server has to
     * render when a fixed-size search box is much finer than one map pixel. */
    private readonly FEATUREINFOMAXWINDOW = 101;

    /**
     * Resolution, in projection units per pixel, the GetFeatureInfo request is
     * built for: the caller's override, otherwise the resolution of the current
     * zoom level. `undefined` when the view has no resolution yet.
     */
    private featureInfoResolution(options?: FeatureInfoOptions): number | undefined {
        const resolution = options?.resolution ?? this.GIS.View.olObject.getResolution();
        if (resolution === undefined || !Number.isFinite(resolution) || resolution <= 0) {
            return undefined;
        }
        return resolution;
    }

    /**
     * Pixel radius sampled around the clicked point. Follows the caller's click
     * tolerance when one is given, so the sampled window and the `BUFFER`
     * parameter cover the same area; otherwise
     * {@link Layer.FEATUREINFORADIUS} is used.
     */
    private featureInfoRadius(tolerance?: number): number {
        if (tolerance === undefined || !Number.isFinite(tolerance)) {
            return this.FEATUREINFORADIUS;
        }
        // Never below one pixel: a `tolerance: 0` window would be a 1x1 image,
        // which servers handle badly (see FEATUREINFOMINWINDOW). BUFFER still
        // carries the caller's zero, so only the click's own pixel is searched.
        return Math.max(Math.round(tolerance), this.FEATUREINFORADIUS);
    }

    /**
     * Sampling window, in pixels, for a fixed-size (`searchSize`) box. Odd, so
     * the click sits on a centre pixel, at least {@link FEATUREINFOMINWINDOW}
     * pixels across, and fine enough that one request pixel is no coarser than
     * one map pixel — otherwise the request would declare a scale coarser than
     * the map and the server's scale-dependent rules could stop reporting the
     * feature that was clicked.
     */
    private searchWindow(halfSpan: number, resolution: number): number {
        const pixels = Math.ceil((2 * halfSpan) / resolution);
        const odd = pixels % 2 === 0 ? pixels + 1 : pixels;
        return Math.min(Math.max(odd, this.FEATUREINFOMINWINDOW), this.FEATUREINFOMAXWINDOW);
    }

    /**
     * Half the side of the queried square, in units of the request projection,
     * when the caller pinned it to a fixed ground size (`searchSize`, in metres).
     * The metres are converted at the clicked coordinate, so the box covers the
     * requested ground size in inflated projections (EPSG:3857) and in
     * geographic ones alike. `undefined` when no metric size was asked for — the
     * window then follows the current zoom level.
     */
    private searchHalfSpan(searchSize: number | undefined, projection: Projection, coordinate: Coordinate): number | undefined {
        if (searchSize === undefined || !Number.isFinite(searchSize) || searchSize <= 0) {
            return undefined;
        }
        const metresPerUnit = getPointResolution(projection, 1, coordinate);
        const unitsPerMetre = Number.isFinite(metresPerUnit) && metresPerUnit > 0
            ? 1 / metresPerUnit
            : 1;
        return (searchSize / 2) * unitsPerMetre;
    }

    /**
     * Projection a GetFeatureInfo request is declared in: the caller's `epsg`
     * first, then a WMS source's own projection (the CRS the layer itself is
     * requested in), and finally the view projection.
     */
    private featureInfoProjection(source: Source, options?: FeatureInfoOptions): Projection {
        const view = this.GIS.View.olObject;
        if (options?.epsg !== undefined && options.epsg !== null && options.epsg !== '') {
            return this.resolveFeatureInfoProjection(options.epsg);
        }
        if (source instanceof TileWMS || source instanceof ImageWMS) {
            return source.getProjection() ?? view.getProjection();
        }
        return view.getProjection();
    }

    /**
     * Resolve the projection a GetFeatureInfo request is built in. Accepts an
     * EPSG code as a number or string (`3067`, `'3067'`, `'EPSG:3067'`) — as
     * everywhere else in the API — or an OpenLayers projection. Defaults to the
     * current view projection.
     *
     * @throws EPSG code is not registered.
     */
    private resolveFeatureInfoProjection(epsg?: FeatureInfoOptions['epsg']): Projection {
        const view = this.GIS.View.olObject;
        if (epsg === undefined || epsg === null || epsg === '') {
            return view.getProjection();
        }
        if (typeof epsg === 'number') {
            return this.GIS.Projection.getRegistered(epsg);
        }
        if (typeof epsg === 'string') {
            // A projection OpenLayers already knows ('EPSG:3067', 'EPSG:4326'),
            // otherwise a bare code registered through GIS.Projection.
            return getOlProjection(epsg) ?? this.GIS.Projection.getRegistered(epsg.replace('EPSG:', ''));
        }
        return epsg;
    }

    /**
     * Convert a view resolution into the resolution of another projection at the
     * given coordinate, so a BBOX requested in a projection other than the view
     * one keeps covering the same amount of ground.
     */
    private convertResolution(resolution: number, from: Projection, to: Projection, coordinate: Coordinate): number {
        if (from === to || from.getCode() === to.getCode()) {
            return resolution;
        }
        const groundResolution = getPointResolution(from, resolution, coordinate);
        const target = transform(coordinate, from, to);
        const metresPerUnit = getPointResolution(to, 1, target);
        if (!Number.isFinite(groundResolution) || !Number.isFinite(metresPerUnit) || metresPerUnit <= 0) {
            return resolution;
        }
        return groundResolution / metresPerUnit;
    }

    /**
     * Build the WMS GetFeatureInfo URL for a clicked coordinate. The BBOX is
     * derived from the resolution of the current zoom level: a small square
     * window of current-zoom pixels around the clicked coordinate, with
     * WIDTH/HEIGHT set to that window and I/J at its centre. One request pixel
     * therefore equals one map pixel at any zoom level — functionally
     * equivalent to a QGIS point query and independent of whether the map has
     * rendered a frame yet. `options.searchSize` swaps that for a square of a
     * fixed ground size, sampled by a window fine enough to keep the request at
     * the map's scale.
     *
     * WMS sources contribute their own parameters (LAYERS, STYLES, VERSION,
     * FORMAT, ENV, …) and CRS; for other sources (e.g. a `wmts` layer backed by
     * a server that also serves WMS GetFeatureInfo) those come from `options`
     * and the view. The WMS endpoint is taken from `options.url`, falling back
     * to the source's own URL when it can be derived.
     */
    private buildFeatureInfoUrl(layerName: string, source: Source, coordinate: Coordinate, options?: FeatureInfoOptions): string | undefined {
        const view = this.GIS.View.olObject;
        const viewResolution = this.featureInfoResolution(options);
        if (viewResolution === undefined) {
            return undefined;
        }
        const sourceParams = this.featureInfoSourceParams(source);

        // Resolve the WMS endpoint: explicit option first, then the source's
        // own URL(s) if it exposes them. Its query string is re-built below, so
        // WMTS KVP parameters cannot leak into the request while a token or key
        // carried by the URL survives.
        const sourceAccess = source as Source & { getUrls?: () => string[] | null; getUrl?: () => string | undefined };
        let endpoint = options?.url;
        if (!endpoint && typeof sourceAccess.getUrls === 'function') {
            endpoint = sourceAccess.getUrls()?.[0];
        }
        if (!endpoint && typeof sourceAccess.getUrl === 'function') {
            endpoint = sourceAccess.getUrl();
        }
        if (!endpoint) {
            throw new Error(`Cannot build a GetFeatureInfo URL for layer '${layerName}': no WMS endpoint available. Provide one via options.url.`);
        }
        const [base, endpointQuery] = [endpoint.split('?')[0], endpoint.split('?')[1]];

        // The declared CRS always matches the BBOX. When it differs from the view
        // projection the click and the resolution are converted into it, so the
        // requested box still covers the same ground.
        const viewProjection = view.getProjection();
        const projection = this.featureInfoProjection(source, options);
        const projectionCode = projection.getCode();
        const requestCoordinate = projection === viewProjection || projectionCode === viewProjection.getCode()
            ? coordinate
            : transform(coordinate, viewProjection, projection);
        const resolution = this.convertResolution(viewResolution, viewProjection, projection, coordinate);
        const version = options?.version ?? this.stringParam(sourceParams.VERSION) ?? '1.3.0';
        const isVersion13 = parseFloat(version) >= 1.3;

        // Square sampling window centred on the click. Two shapes:
        //  - `searchSize` pins the BBOX to a fixed ground size, sampled by a
        //    window fine enough to keep the request's scale that of the map, and
        //    a BUFFER that reaches the edges of the box.
        //  - otherwise the BBOX is measured in pixels of the *current zoom level*
        //    — it spans windowSize * resolution, so one request pixel equals one
        //    map pixel and the queried area follows the zoom level (metres when
        //    zoomed in) instead of being a fixed, kilometre-wide square.
        const metricHalfSpan = this.searchHalfSpan(options?.searchSize, projection, requestCoordinate);
        const windowSize = metricHalfSpan === undefined
            ? 2 * this.featureInfoRadius(options?.tolerance) + 1
            : this.searchWindow(metricHalfSpan, resolution);
        const halfSpan = metricHalfSpan ?? (windowSize / 2) * resolution;
        // Ground size of one request pixel, whichever shape the window has.
        const pixelSpan = (2 * halfSpan) / windowSize;
        // Pixel radius searched around the click. A fixed-size box searches out
        // to its own edges; otherwise it is the caller's tolerance, if any.
        const buffer = metricHalfSpan === undefined
            ? options?.tolerance
            : (windowSize - 1) / 2;
        const minX = requestCoordinate[0] - halfSpan;
        const maxX = requestCoordinate[0] + halfSpan;
        const minY = requestCoordinate[1] - halfSpan;
        const maxY = requestCoordinate[1] + halfSpan;
        // WMS 1.3.0 follows the CRS axis order: for north-first systems (e.g.
        // EPSG:4326) the BBOX is miny,minx,maxy,maxx.
        const northFirst = isVersion13 && projection.getAxisOrientation().charAt(0).toLowerCase() === 'n';
        const boundingBox = northFirst
            ? [minY, minX, maxY, maxX]
            : [minX, minY, maxX, maxY];

        // Pixel column (I/X) and row (J/Y) of the clicked coordinate within the
        // WIDTHxHEIGHT image — the centre pixel of the window. Image rows run
        // top-down, so J is measured from the north (maxY) edge. Independent of
        // CRS axis order.
        const columnPixel = Math.floor((requestCoordinate[0] - minX) / pixelSpan);
        const rowPixel = Math.floor((maxY - requestCoordinate[1]) / pixelSpan);

        // The layer's own LAYERS value (WMS sources) or the name it is registered
        // under in the map. Override with `layers` when the server's WMS layer
        // name differs from both.
        const layers = this.joinLayers(options?.layers)
            ?? this.joinLayers(options?.queryLayers)
            ?? this.stringParam(sourceParams.LAYERS)
            ?? layerName;
        const queryLayers = this.joinLayers(options?.queryLayers) ?? layers;

        // Start from the parameters the layer itself is drawn with (ENV, TOKEN,
        // CQL filters, …) so the query returns what the user sees, then set the
        // GetFeatureInfo request and its geometry on top of them.
        const params: { [key: string]: string } = this.endpointParams(endpointQuery);
        Object.assign(params, this.wmsParams(sourceParams), {
            SERVICE: 'WMS',
            VERSION: version,
            REQUEST: 'GetFeatureInfo',
            LAYERS: layers,
            QUERY_LAYERS: queryLayers,
            STYLES: options?.styles ?? this.stringParam(sourceParams.STYLES) ?? '',
            FORMAT: options?.format ?? this.stringParam(sourceParams.FORMAT) ?? 'image/png',
            INFO_FORMAT: options?.infoFormat ?? 'text/html',
            WIDTH: String(windowSize),
            HEIGHT: String(windowSize),
            BBOX: boundingBox.join(',')
        });
        if (isVersion13) {
            params.CRS = projectionCode;
            params.I = String(columnPixel);
            params.J = String(rowPixel);
        } else {
            params.SRS = projectionCode;
            params.X = String(columnPixel);
            params.Y = String(rowPixel);
        }
        if (options?.featureCount !== undefined) {
            params.FEATURE_COUNT = String(options.featureCount);
        }
        if (buffer !== undefined) {
            params.BUFFER = String(buffer);
        }

        return `${base}?${new URLSearchParams(params).toString()}`;
    }

    /**
     * Query a WMS layer for feature details at a coordinate and return the
     * server response. Builds the URL via {@link Layer.getFeatureInfoUrl},
     * performs a fetch and resolves with the response body (HTML by default).
     *
     * @param layerName - Name of the layer as registered in the map.
     * @param coordinate - Clicked coordinate in the view projection.
     * @param options - {@link FeatureInfoOptions}.
     * @param callback - Optional callback receiving the response body, or
     *   `null` on failure / when no URL could be built.
     * @returns A promise resolving with the response body, or `null`.
     */
    public getFeatureInfo(layerName: string, coordinate: Coordinate, options?: FeatureInfoOptions, callback?: FeatureInfoCallback): Promise<string | null> {
        const url = this.getFeatureInfoUrl(layerName, coordinate, options);
        if (!url) {
            callback?.(null);
            return Promise.resolve(null);
        }

        const fetchOptions: FetchOptions = {};
        if (options?.headers) {
            fetchOptions.headers = options.headers;
        }

        return fetch(url, fetchOptions)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`GetFeatureInfo request failed with status ${response.status}.`);
                }
                return response.text();
            })
            .then(body => {
                callback?.(body);
                return body;
            })
            .catch(error => {
                console.error('[Layer.getFeatureInfo] request failed', error);
                callback?.(null);
                return null;
            });
    }

    private checkIdAttribute(feature: Feature, attribute: string) {
        if (feature.getKeys().filter(key => key === attribute).length === 0) {
            throw Error('Feature does not have this id property.')
        }
    }

    private validateExtent(source: Geometry | LayerType): Extent | undefined {
        if (!source || typeof source.getExtent !== 'function') {
            return undefined;
        }

        try {
            const extent = source.getExtent();
            if (Array.isArray(extent) && extent.length === 4 && !isEmpty(extent)) {
                return extent;
            }
            return undefined;
        } catch {
            return undefined;
        }
    }

    private resolveLayerExtent(layerName: string, callback?: GetExtent): Extent {
        const layer = this.getLayer(layerName);
        const source = layer.getSource();

        const extent = this.validateExtent(layer) ?? this.validateExtent(source);

        if (!extent) {
            throw new Error(`No valid extent found for layer ${layerName}.`);
        }

        if (extent.length === 4) {
            callback?.(extent[0], extent[1], extent[2], extent[3]);
        }

        return extent;
    }

    private resolveEffectiveExtent(layerName: string, callback?: GetExtent): Extent {
        const layer = this.getLayer(layerName);

        const zoomExtent = layer.get('zoomExtent');
        if (Array.isArray(zoomExtent) && zoomExtent.length === 4 && !isEmpty(zoomExtent)) {
            const validZoomExtent = zoomExtent;
            callback?.(validZoomExtent[0], validZoomExtent[1], validZoomExtent[2], validZoomExtent[3]);
            return validZoomExtent;
        }

        return this.resolveLayerExtent(layerName, callback);
    }

    /**
     * Get the actual extent of a layer or its source.
     * This does not consider any custom 'zoomExtent' property.
     *
     * @param layerName - Name of the layer in the map.
     * @param callback - Optional callback to receive the extent as (minx, miny, maxx, maxy).
     * @example
     * Layer.getLayerExtent('position', (minx, miny, maxx, maxy) => {
     *     console.log(minx, maxx);
     * });
     * @throws Will throw an error if:
     * - No such layer exists on the map.
     * - Neither the layer nor its source has a valid extent.
     * @returns A 4-element extent array: [minx, miny, maxx, maxy].
     */
    public getLayerExtent(layerName: string, callback?: GetExtent) {
        return this.resolveLayerExtent(layerName, callback);
    }

    /**
     * Get the effective extent of a layer.
     * Tries to use the custom 'zoomExtent' (if set on the layer),
     * otherwise falls back to the actual layer/source extent.
     *
     * @param layerName - Name of the layer in the map.
     * @param callback - Optional callback to receive the extent as (minx, miny, maxx, maxy).
     * @example
     * Layer.getEffectiveExtent('position', (minx, miny, maxx, maxy) => {
     *     console.log(`Effective extent: ${minx} to ${maxx}`);
     * });
     * @throws Will throw an error if:
     * - No such layer exists on the map.
     * - Neither zoomExtent nor actual extent is valid.
     * @returns A 4-element extent array: [minx, miny, maxx, maxy].
     */
    public getLayerEffectiveExtent(layerName: string, callback?: GetExtent) {
        return this.resolveEffectiveExtent(layerName, callback);
    }

    /**
     * @private
     * @param layerName of a layer
     * @param attribute ID property name
     * @param id ID value
     *
     * @returns Ol Layer
     */
    getOlFeatureByAttributeID(layerName: string, attribute: string, id: number | string) {
        const layer = this.getLayer(layerName);
        const source = layer.getSource();
        return source.forEachFeature((feature: Feature) => {
            this.checkIdAttribute(feature, attribute);

            if (String(feature.get(attribute)) === String(id)) {
                return feature;
            }
        })
    }

    public transformCoordinates(layerName: string, xy: import("ol/coordinate").Coordinate, epsg: string | number) {
        const source = this.getLayer(layerName).getSource();
        const destinationProjection = this.GIS.Projection.getRegistered(epsg);
        let sourceProjection = source.getProjection() || this.GIS.View.olObject.getProjection();

        return transform(xy, sourceProjection, destinationProjection);
    }

    /**
     * Get feature center by ID
     * Center is derived from Extent, it might not fall in the middle of polygon
     * @param name Layer name
     * @param id - id as string or number
     * @param options
     * @param callback
     * @example Layer.getFeatureCenterByID('layer-name', 1, {attribute: 'id'} (x, y) => {console.log(x)})
     * @throws No such layer in the map.
     * @throws Empty extent.
     * @throws Does not have an extent.
     * @throws Could not find feature.
     * @trows {Error} Feature does not have this id property.
     * @returns X coordinate, Y coordinate & M is the length of the horizontal intersection that the point belongs to
     */
    public getFeatureCenterByID(name: string, id: string | number, options?: GetFeatureCenterByIDOptions, callback?: GetXYM) {
        let idProperty = getIdProperty(options as GetIdPropertyOptions);

        const feature = this.getOlFeatureByAttributeID(name, idProperty, id);

        if (!feature) {
            throw new Error('Could not find feature.');
        }

        const geometry = feature.getGeometry()

        const extent = this.validateExtent(geometry);

        let center;
        const geometryType = geometry.getType()

        if (options && options.type === 'interior') {
            switch (geometryType) {
                case 'Polygon':
                    center = geometry.getInteriorPoint().getCoordinates();
                    break;
                case 'MultiPolygon':
                    center = geometry.getInteriorPoints().getCoordinates();
                    break;
            }
        } else {
            if (extent) {
                center = getCenter(extent);
            }
        }

        if (options && options.epsg) {
            switch (geometryType) {
                case 'Polygon':
                    center = this.transformCoordinates(name, center, options.epsg);
                    break;
                case 'MultiPolygon':
                    center = center.map((xym: import("ol/coordinate").Coordinate) => {
                        return options.epsg
                            ? this.transformCoordinates(name, xym, options.epsg)
                            : xym;
                    })
                    break;
            }
        }

        let result = []
        if (center[0] && center[0] instanceof Array) {
            result = center.map((xym: import("ol/coordinate").Coordinate) => {
                return {
                    x: xym[0],
                    y: xym[1],
                    m: xym[2]
                }
            })
        } else if (center.length === 3) {
            result = [{
                x: center[0],
                y: center[1],
                m: center[2]
            }]
        } else {
            result = [{
                x: center[0],
                y: center[1]
            }]
        }

        if (callback) {
            callback(result);
        }

        return result;
    };

    public getFeatureExtent(name: string, id: number | string, options?: GetIdPropertyOptions & EPSGOptions) {
        let idProperty = getIdProperty(options);

        const feature = this.getOlFeatureByAttributeID(name, idProperty, id);

        if (!feature) {
            throw new Error('Could not find feature.');
        }

        const geometry = feature.getGeometry()

        let extent = this.validateExtent(geometry) as Extent;

        if (options && options.epsg) {
            const source = this.getLayer(name).getSource();
            const destinationProjection = this.GIS.Projection.getRegistered(options.epsg);
            let sourceProjection = source.getProjection() || this.GIS.View.olObject.getProjection();
            extent = transformExtent(extent, sourceProjection, destinationProjection);
        }

        return extent;
    }

    /**
     * Get feature extent by ID
     * @param name Layer name
     * @param id id or ids for example: 1, '1' or [1, 2, 3]
     * @param options
     * @param callback
     * @example Layer.getFeatureExtentByID('layer-name', 1, {attribute: 'id'} (minx, miny, maxx, maxy) => {console.log(minx)})
     * @throws No such layer in the map.
     * @throws Empty extent.
     * @throws Does not have an extent.
     * @throws Could not find feature.
     * @trows Feature does not have this id property.
     * @returns Extent array [minx, miny, maxx, maxy]
     */
    public getFeatureExtentByID(name: string, id: number | string | (string | number)[], options?: GetFeatureExtentByIDOptions, callback?: GetExtent) {

        let extent = createEmpty();

        if (id instanceof Array) {
            id.forEach(i => {
                const ext = this.getFeatureExtent(name, i, options as GetIdPropertyOptions);
                extend(extent, ext);
            })
        } else {
            extent = this.getFeatureExtent(name, id as number | string, options as GetIdPropertyOptions);
        }

        if (callback) {
            callback(extent[0], extent[1], extent[2], extent[3]);
        }

        return extent;
    }

    /**
     *
     * @param name - name of the layer
     * @param id - value of the ID or attribute
     * @param options
     * @param callback
     * @example GIS.Layer.getFeatureByID('point', 1, {attribute: 'id', format:'wkt', epsg: 'EPSG:4326'}, geometry => {console.log(geometry)});
     * @throws No such layer in the map.
     * @throws Unknown geometry format.
     * @throws Feature does not have this id property.
     * @throws Could not find feature.
     * @returns {*}
     */
    public getFeatureByID(name: string, id: string | number, options?: GetFeatureByIDOptions, callback?: GeometryString) {
        let idProperty = getIdProperty(options as GetIdPropertyOptions);

        const feature = this.getOlFeatureByAttributeID(name, idProperty, id);

        if (!feature) {
            throw new Error('Could not find feature.');
        }

        let geometry;
        let geometryRepresentation;

        const drawnGeometry = feature.getGeometry();
        const drawGeometryType = drawnGeometry.getType();

        if (drawGeometryType === 'Circle') {
            geometry = this.GIS.Projection.circleToPolygon(drawnGeometry);
        } else {
            geometry = drawnGeometry;
        }

        const writeOptions: WriteOptions = {
            dataProjection: options && options.epsg ? options.epsg as ProjectionLike : this.GIS.View.olObject.getProjection(),
            featureProjection: this.GIS.View.olObject.getProjection()
        }

        if (options && options.format) {
            switch (options.format.toLowerCase()) {
                case this.format.WKT:
                    geometryRepresentation = new WKT().writeGeometry(geometry, writeOptions);
                    break;
                case this.format.GEOJSON:
                    geometryRepresentation = new GeoJSON().writeGeometry(geometry, writeOptions);
                    break;
                case this.format.XY:
                    geometryRepresentation = geometry.getCoordinates();
                    break;
                case this.format.SIMPLE:
                    geometryRepresentation = geometry;
                    break;
                default:
                    throw new Error('Unknown geometry format.');
            }
        } else {
            throw new Error('Unknown geometry format.');
        }

        if (callback) {
            callback(geometryRepresentation)
        }

        return geometryRepresentation;
    }

    /**
     * Set feature(s) style by ID.
     * ID field can be defined by attribute.
     * @param name Layer name
     * @param style Style name
     * @param id ID value for the feature.
     * @param options
     * @throws Feature does not have this id property.
     * @throws No such layer in the map.
     * @throws No such style defined.
     */
    public setFeatureStyleByID(name: string, style: string, id: string | number, options?: GetIdPropertyOptions) {
        const idProperty = getIdProperty(options);
        const layer = this.getLayer(name);
        const source = layer.getSource();
        const olStyle = this.GIS.Style.getStyle(style) as StyleLike | undefined;

        const features = getFeaturesFromSource(source, idProperty, id);

        features.forEach((feature: Feature) => {
            feature.setStyle(olStyle);
            feature.set(this.CURRENT_STYLENAME, style);
        });
    }

    /**
     * Get feature style name by ID.
     * StyleName is defined in Style config.
     * @param name Layer name
     * @param id ID value for the feature.
     * @param options
     * @param callback
     * @returns @returns {string} Name of the style.
     */
    public getFeatureStyleNameByID(name: string, id: string | number, options?: GetIdPropertyOptions, callback?: StyleNameString) {
        let idProperty = getIdProperty(options);

        const feature = this.getOlFeatureByAttributeID(name, idProperty, id);

        let styleName;
        if (feature) {
            styleName = feature.get(this.CURRENT_STYLENAME);
        }

        if (callback) {
            callback(styleName);
        }

        return styleName;
    }

    /**
     * Reset feature style to default, defined in config.
     * @param name Layer name
     * @param IDs - Array of features to reset style on them. If IDs is undefined resets all features in the layer !
     * @param options
     * @throws Feature does not have this id property.
     * @throws No such layer in the map.
     * @throws No such style defined.
     */
    public resetLayerStyleToDefault(name: string, IDs?: (string | number)[], options?: GetIdPropertyOptions) {
        const layer = this.getLayer(name);

        let idProperty = getIdProperty(options);

        const activeStyleName = layer.get(this.ACTIVE_STYLENAME) || layer.get(this.DEFAULT_STYLENAME);
        const source = layer.getSource()

        source.forEachFeature((feature: Feature) => {
            this.checkIdAttribute(feature, idProperty);

            const resetFeature = (feature: Feature) => {
                // Clear feature-level style override so it inherits the layer-level style
                feature.setStyle(undefined);
                feature.set(this.CURRENT_STYLENAME, activeStyleName);
            };

            if (IDs) {
                IDs.map(ID => {
                    if (String(feature.get(idProperty)) === String(ID)) {
                        resetFeature(feature);

                        const index = IDs.indexOf(ID);
                        IDs.splice(index, 1);
                    }
                })
            } else {
                resetFeature(feature);
            }
        })
    }

    /**
     * Get feature IDs in Extent
     * Ol method getFeaturesInExtent {@link https://openlayers.org/en/latest/apidoc/module-ol_source_Vector-VectorSource.html#getFeaturesInExtent}
     * Returns an array of features intersects the extent of view or provided.
     * @param name Layer name
     * @param extent Extent array [minx, miny, maxx, maxy]. Please add null, when view extent is needed.
     * @param options
     * @param callback
     * @throws Attribute is not present in the layer.
     */
    public getFeaturesIDsInExtent(name: string, extent: Extent, options?: GetFeaturesIDsInExtentOptions, callback?: GetIDs) {
        const layer = this.getLayer(name);
        const source = layer.getSource();
        const viewEpsg = Number(this.GIS.View.olObject.getProjection().getCode().replace('EPSG:', ''));
        let intersectionExtent: string | Extent | undefined;
        if (!extent) {
            intersectionExtent = this.GIS.View.getExtent();

            if (options && options.epsg && options.epsg !== viewEpsg) {
                delete options.epsg;
                console.warn('Please don\'t use epsg with empty extent.');
            }
        } else {
            intersectionExtent = extent;
        }

        let IDs: (string | number)[] = [];
        let attribute = getIdProperty(options);

        if (options && options.epsg) {
            intersectionExtent = transformExtent(extent, this.GIS.Projection.getRegistered(options.epsg), this.GIS.Projection.getRegistered(viewEpsg));
        }

        const features = source.getFeaturesInExtent(intersectionExtent, this.GIS.Projection.getRegistered(viewEpsg));
        features.map((feature: Feature) => {
            const value: string | number = feature.get(attribute);
            if (value === undefined) {
                throw new AttributeMissingError('Attribute is not present in the layer.');
            }
            IDs.push(value);
        });

        if (callback) {
            callback(IDs);
        }

        return IDs;
    };

    /**
     * Reports whether a layer is currently rendered given the active view,
     * mirroring OpenLayers' own in-view test: the layer must be visible and
     * the current resolution / zoom must fall inside the layer's configured
     * min/max range. Used to keep the map-window feature service in step with
     * what the user actually sees at the current zoom level.
     */
    private isLayerInView(layer: BaseLayer): boolean {
        if (!layer.getVisible()) {
            return false;
        }

        const view = this.GIS.View.olObject;
        const resolution = view.getResolution();

        // Without a resolution the view is not ready to constrain anything —
        // fall back to the layer's visibility flag alone.
        if (resolution === undefined) {
            return true;
        }

        if (resolution < layer.getMinResolution() || resolution >= layer.getMaxResolution()) {
            return false;
        }

        const zoom = view.getZoom();
        if (zoom !== undefined && (zoom <= layer.getMinZoom() || zoom > layer.getMaxZoom())) {
            return false;
        }

        return true;
    }

    /**
     * Filter the features of a layer by the current spatial boundaries of the
     * map window (or by an explicitly supplied extent) and return their data.
     *
     * Unlike {@link getFeaturesIDsInExtent}, which only returns ids, this
     * service returns each feature's id, attributes and geometry — ready to
     * populate a side panel listing, for example, the forest compartments
     * visible in the current viewport.
     *
     * The query respects the selected layer definition: by default a layer
     * that is not rendered at the current zoom (hidden, or outside its
     * configured min/max zoom / resolution range) yields an empty result, so
     * callers never receive data the user cannot see. Pass
     * `respectLayerDefinition: false` to query the raw source regardless of
     * what is currently drawn.
     *
     * Feature lookup is backed by the source's spatial index
     * (`getFeaturesInExtent`), keeping retrieval fast across varying zoom
     * levels.
     *
     * @param name Layer name — the active layer to query.
     * @param extent Extent array `[minx, miny, maxx, maxy]`. Pass `null` / omit to use the current map window extent.
     * @param options
     * @param callback
     * @throws No such layer in the map.
     * @throws Layer has no vector source.
     * @throws Unknown geometry format.
     * @returns Array of `{ id, attributes, geometry }` for every feature inside the extent.
     * @example
     * // Forest compartments currently visible in the map window
     * GIS.Layer.getFeaturesInExtent('forest_compartment', null, {attribute: 'compartmentId'});
     * // Features inside a given extent, geometry as WKT in EPSG:4326
     * GIS.Layer.getFeaturesInExtent('forest_compartment', [1345852.64, 6203894.35, 2099485.60, 6631913.89], {format: 'wkt', epsg: 4326}, features => console.log(features));
     */
    public getFeaturesInExtent(name: string, extent?: Extent | null, options?: GetFeaturesInExtentOptions, callback?: GetFeaturesInExtent): FeatureInExtent[] {
        const layer = this.getLayer(name);
        const source = layer.getSource();

        if (!source || typeof (source as any).getFeaturesInExtent !== 'function') {
            throw new Error('Layer has no vector source.');
        }

        const format = (options?.format ?? this.format.GEOJSON).toLowerCase();
        if (![this.format.GEOJSON, this.format.WKT, this.format.XY, this.format.SIMPLE].includes(format)) {
            throw new Error('Unknown geometry format.');
        }

        const respectLayerDefinition = options?.respectLayerDefinition ?? true;
        if (respectLayerDefinition && !this.isLayerInView(layer)) {
            if (callback) {
                callback([]);
            }
            return [];
        }

        const viewProjection = this.GIS.View.olObject.getProjection();
        const viewEpsg = Number(viewProjection.getCode().replace('EPSG:', ''));

        let intersectionExtent: Extent;
        if (!extent) {
            intersectionExtent = this.GIS.View.olObject.calculateExtent();
            if (options && options.epsg && options.epsg !== viewEpsg) {
                console.warn('Please don\'t use epsg with empty extent.');
            }
        } else if (options && options.epsg) {
            intersectionExtent = transformExtent(extent, this.GIS.Projection.getRegistered(options.epsg), viewProjection);
        } else {
            intersectionExtent = extent;
        }

        const idProperty = getIdProperty(options as GetIdPropertyOptions);

        const writeOptions: WriteOptions = {
            dataProjection: options && options.epsg ? this.GIS.Projection.getRegistered(options.epsg) : viewProjection,
            featureProjection: viewProjection
        };

        const wktFormatter = format === this.format.WKT ? new WKT() : undefined;
        const geoJsonFormatter = format === this.format.GEOJSON ? new GeoJSON() : undefined;

        const features = (source as VectorSource<Feature<Geometry>>).getFeaturesInExtent(intersectionExtent, viewProjection);

        const result: FeatureInExtent[] = features.map((feature: Feature<Geometry>) => {
            const geometryName = feature.getGeometryName();
            // getProperties() includes the geometry under its property name;
            // keep the returned attributes bag geometry-free.
            const {[geometryName]: _omittedGeometry, ...attributes} = feature.getProperties();

            const rawGeometry = feature.getGeometry();
            let geometry: FeatureInExtent['geometry'];
            if (rawGeometry) {
                const normalizedGeometry = rawGeometry.getType() === 'Circle'
                    ? this.GIS.Projection.circleToPolygon(rawGeometry as Circle)
                    : rawGeometry;
                switch (format) {
                    case this.format.WKT:
                        geometry = wktFormatter!.writeGeometry(normalizedGeometry, writeOptions);
                        break;
                    case this.format.GEOJSON:
                        geometry = geoJsonFormatter!.writeGeometry(normalizedGeometry, writeOptions);
                        break;
                    case this.format.XY:
                        geometry = (normalizedGeometry as any).getCoordinates?.();
                        break;
                    case this.format.SIMPLE:
                        geometry = normalizedGeometry;
                        break;
                }
            }

            return {
                id: attributes[idProperty],
                attributes,
                geometry
            };
        });

        if (callback) {
            callback(result);
        }

        return result;
    };

    /**
     * Get feature IDs by coordinate in radius specified.
     * It tries to find distance from features center.
     * The output array is sorted by distance.
     * Returns an array of overlapping features.
     * @param name Layer name
     * @param coordinate X,Y coordinates in view's coordinate system or defined in options.
     * @param radius Radius in meters.
     * @param options
     * @param callback
     * @throws Attribute is not present in the layer.
     */
    public getFeaturesIDsInRadius(name: string, coordinate: import("ol/coordinate").Coordinate, radius: number, options?: GetFeaturesIDsInRadiusOptions, callback?: GetIDs) {
        const layer = this.getLayer(name);
        const source = layer.getSource();

        let featuresWithDistances: LayerIdWithDistance[] = [];
        let attribute = getIdProperty(options);
        let viewCoordinate = coordinate;

        if (options && options.epsg) {
            viewCoordinate = transform(coordinate, options.epsg, this.GIS.View.olObject.getProjection());
        }

        const extent = buffer([viewCoordinate[0], viewCoordinate[1], viewCoordinate[0], viewCoordinate[1]], radius * 2.5);

        source.forEachFeatureIntersectingExtent(extent, (feature: Feature<Geometry>) => {
            const geometry = feature.getGeometry();
            if (!geometry) {
                return;
            }

            let distance;
            if (geometry.getType() === 'Point') {
                const featureCoordinate = (geometry as Point).getCoordinates();
                const line = new LineString([featureCoordinate, viewCoordinate]);
                distance = getLength(line, options?.epsg as SphereMetricOptions);
            } else if (geometry.getType() === 'LineString') {
                const closestPoint = geometry.getClosestPoint(viewCoordinate);
                const line = new LineString([closestPoint, viewCoordinate]);
                distance = getLength(line, options?.epsg as SphereMetricOptions);
            } else if (geometry.getType() === 'Polygon') {
                if (geometry.intersectsCoordinate(viewCoordinate)) {
                    distance = 0;
                } else {
                    const closestPoint = geometry.getClosestPoint(viewCoordinate);
                    const line = new LineString([closestPoint, viewCoordinate]);
                    distance = getLength(line, options?.epsg as SphereMetricOptions);
                }
            } else {
                return;
            }

            if (distance < radius) {
                const value = feature.get(attribute);
                if (!value) {
                    throw new Error('Attribute is not present in the layer.');
                }
                featuresWithDistances.push({id: value, distance});
            }
        });

        featuresWithDistances.sort((a, b) => a.distance - b.distance);

        const IDs = featuresWithDistances.map(item => item.id);

        if (callback) {
            callback(IDs);
        }

        return IDs;
    };

    /**
     * Get feature IDs by coordinate.
     * Returns an array of overlapping features.
     * @param name Layer name
     * @param coordinate X,Y coordinates in view's coordinate system or defined in options.
     * @param options
     * @param callback
     * @throws Attribute is not present in the layer.
     */
    public getFeatureIDsAtCoordinate(name: string, coordinate: import("ol/coordinate").Coordinate, options?: GetFeatureIDsAtCoordinateOptions, callback?: GetIDs) {
        const layer = this.getLayer(name);
        const source = layer.getSource();

        let features;
        let IDs: (string | number)[] = [];
        let attribute = getIdProperty(options);
        let viewCoordinate = coordinate;

        if (options && options.epsg) {
            viewCoordinate = transform(coordinate, options.epsg, this.GIS.View.olObject.getProjection());
        }

        let closest = false;
        let tolerance;

        if (options && options.strategy) {
            if (options.strategy === 'closest') {
                closest = true;
            }

            if (options.tolerance) {
                tolerance = options.tolerance;
            }
        }

        try {
            if (closest) {
                let filter;
                if (options?.filter) {
                    filter = (feature: Feature) => {
                        return String(feature.get(options.filter!.attribute)) === String(options.filter!.equal);
                    };
                }
                const feature = source.getClosestFeatureToCoordinate(viewCoordinate, filter)
                features = [feature]

                if (tolerance) {
                    const featureCoordinates = feature.getGeometry().getCoordinates();
                    const line = new LineString([featureCoordinates, viewCoordinate]);

                    if (line.getLength() > tolerance) {
                        features = [];
                    }
                }
            } else {
                features = source.getFeaturesAtCoordinate(viewCoordinate)
            }

            features.map((feature: Feature) => {
                const value = feature.get(attribute);
                if (value === undefined) {
                    throw new AttributeMissingError('Attribute is not present in the layer.');
                }
                IDs.push(value);
            });
        } catch (error) {
            if (error instanceof AttributeMissingError) {
                throw error;
            }
            console.warn('Could not find features.');
        }

        if (callback) {
            callback(IDs);
        }

        return IDs;
    };

    /**
     * Get feature's attribute by ID.
     * @param name Layer name
     * @param attribute Attribute name
     * @param id feature's id
     * @param options
     * @param callback
     */
    public getFeatureAttributeByID(name: string, attribute: string, id: string | number, options?: GetIdPropertyOptions, callback?: Attribute) {
        let idProperty = getIdProperty(options);

        const feature = this.getOlFeatureByAttributeID(name, idProperty, id);

        if (callback && feature) {
            callback(feature.get(attribute));
        } else if (callback) {
            callback(null);
        }

        if (feature) {
            return feature.get(attribute);
        } else {
            return null;
        }
    }

    /**
     * Get feature's attributes by ID.
     * @param name Layer name
     * @param id feature's id
     * @param options
     * @param callback
     */
    public getFeatureAttributesByID(name: string, id: string | number, options?: GetIdPropertyOptions, callback?: Attributes): {[p: string]: any} | null {
        let idProperty = getIdProperty(options);

        const feature = this.getOlFeatureByAttributeID(name, idProperty, id);

        if (callback && feature) {
            callback(feature.getProperties());
        } else if (callback) {
            callback(null);
        }

        if (feature) {
            return feature.getProperties();
        } else {
            return null;
        }
    }

    /**
     * Set feature's attribute by ID.
     * When attribute doesn't exist, it will be created.
     * @param name Layer name
     * @param attribute Attribute name
     * @param value Value to be set
     * @param id feature's id
     * @param options
     * @throws Feature does not have this id property.
     */
    public setFeatureAttributeByID(name: string, attribute: string, value: string | number, id: string | number, options?: GetIdPropertyOptions) {
        let idProperty = getIdProperty(options);

        const feature = this.getOlFeatureByAttributeID(name, idProperty, id);

        if (feature) {
            feature.set(attribute, value);
        } else {
            console.warn("Cannot find the feature: " + value)
        }
    }

    /**
     * Set feature's attributes by ID.
     * When some attribute doesn't exist, it will be created.
     * @param name Layer name
     * @param data Data used to update
     * @param options
     * @throws Feature does not have this id property.
     */
    public setFeatureAttributesByID(name: string, data: any[], options?: GetIdPropertyOptions & RewriteOptions) {
        let idProperty = getIdProperty(options);

        const layer = this.getLayer(name);
        const source = layer.getSource();

        source.forEachFeature((feature: Feature) => {
            this.checkIdAttribute(feature, idProperty);

            let entry = data.find(property => String(property[idProperty]) === String(feature.get(idProperty)));

            if (!entry && options && options.rewrite && data.length > 0) {
                entry = Object.assign({}, data[0]);
                for (const [key] of Object.entries(entry)) {
                    if (key === idProperty) {
                        delete entry[key];
                    } else {
                        entry[key] = null;
                    }
                }
            }

            if (entry) {
                feature.setProperties(entry);
            }
        })
    }

    /**
     * Get collection of attributes of all features.
     * Return empty array, when layer doesn't contain any feature.
     * @param name Layer name
     * @returns attributes collection (based on getProperties function from OL) {@link https://openlayers.org/en/latest/apidoc/module-ol_Feature-Feature.html#getProperties}
     */
    public getAttributes(name: string) {
        const layer = this.getLayer(name);
        const source = layer.getSource();

        const propertiesArray: { [x: string]: any; } = [];

        source.forEachFeature((feature: Feature) => {
            const properties = feature.getProperties();
            propertiesArray.push(properties);
        });

        return propertiesArray;
    }

    /**
     * Get feature IDs by coordinate.
     * Returns an array of overlapping features.
     * @param name Layer name
     * @param attribute Attribute name
     * @param options
     * @param callback
     * @throws Feature does not have this property name.
     * @throws Attribute is not present in the layer.
     */
    public getFeatureIDsByAttribute(name: string, attribute: string, options?: GetFeatureIDsByAttributeOptions, callback?: GetIDs) {
        const layer = this.getLayer(name);
        const source = layer.getSource();

        const IDs: (string | number)[] = [];

        let idProperty = getIdProperty(options);

        if (options?.equal) {
            source.forEachFeature((feature: Feature) => {
                const properties = feature.getProperties();
                if (!properties.hasOwnProperty(attribute)) {
                    throw new Error('Feature does not have this property name.');
                }

                if (String(feature.get(attribute)) === String(options.equal)) {
                    const value = feature.get(idProperty);
                    if (!value) {
                        throw new Error('Attribute is not present in the layer.');
                    }
                    IDs.push(value)
                }
            })
        }

        if (callback) {
            callback(IDs);
        } else {
            return IDs;
        }
    };

    private redrawPointToCircle(layer: BaseLayer, features: Feature<Geometry>[]) {
        const attributeName = layer.get('shape').radius;
        const redrawnFeatures: Feature[] = [];
        features.forEach((feature: Feature) => {
            const geometry: Point | undefined = feature.getGeometry() as Point;
            if (geometry) {
                const coordinates: Coordinate = geometry.getCoordinates();
                const circle = coordinates ? new Circle(coordinates, feature.get(attributeName)) : null;
                if (circle) {
                    feature.setGeometry(circle as Geometry);
                    const polygon = new Feature({
                        name: "circle",
                        geometry: circle
                    });
                    redrawnFeatures.push(polygon);
                }
            }
        });

        return redrawnFeatures;
    }

    /**
     * Add features to the layer
     * Added features are pushed to the layer and do not replace them
     * @param name Layer name
     * @param features GeoJSON object. Its coordinate system is taken from the
     *   `crs` member when present (standard named form, legacy EPSG/link forms
     *   or the string/number shortcuts) and from the view projection otherwise.
     *   An explicit `epsg` option overrides both.
     * @param options
     * @throws No such layer in the map.
     * @throws Unsupported GeoJSON type: undefined
     * @throws GeoJSON has to be specified.
     * @throws CRS is not specified neither in GeoJSON nor in the options parameter.
     * @returns Number of features
     */
    public addFeatures(name: string, features: GeoJSONData | GeoJSONWithCRS, options?: FormatAndEPSGOptions) {
        const layer = this.getLayer(name);

        let olFormat: GeoJSON | WKT | GML3 | undefined;
        let format = this.format.GEOJSON;

        if (options && options.format) {
            format = options.format;
        }

        switch (format) {
            case this.format.WKT:
                olFormat = new WKT();
                break;
            case this.format.GEOJSON:
                olFormat = new GeoJSON();
                break;
            case this.format.GML3:
                olFormat = new GML3();
                break;
        }

        if (!features) {
            throw new Error('GeoJSON has to be specified.');
        }

        let dataProjection;

        if (options && options.epsg) {
            dataProjection = this.getDeclaredProjection(options.epsg);
        } else if (format === this.format.WKT || format === this.format.GML3) {
            dataProjection = this.GIS.View.olObject.getProjection();
        } else {
            // GeoJSON can declare its coordinate system in any of the notations
            // GeoJSONCRS understands - the standard named form, the legacy
            // EPSG/link forms and the string/number shortcuts. When nothing is
            // declared the coordinates are already in the view projection and
            // are read as-is.
            const crs = GeoJSONCRS.fromGeoJSON(features);
            dataProjection = crs
                ? this.getDeclaredProjection(crs.code)
                : this.GIS.View.olObject.getProjection();
        }

        const geojson: Feature<Geometry>[] = olFormat?.readFeatures(features, {
            dataProjection: dataProjection,
            featureProjection: this.GIS.View.olObject.getProjection(),
        }) as Feature<Geometry>[];

        const numberOfFeatures = geojson?.length;

        if (numberOfFeatures === 0) {
            console?.warn('Data doesn\'t have any features to add.');
            return 0;
        }
        const source = layer.getSource();

        //GeoJSON doesn't support
        if (geojson && layer.get('shape') && layer.get('shape').type === 'circle') {
            const polygon = this.redrawPointToCircle(layer, geojson);
            source.addFeatures(polygon);
            this.enableAutoMeasurement(layer, polygon);
            return numberOfFeatures;
        } else {
            source.addFeatures(geojson);
            this.enableAutoMeasurement(layer, geojson);
            return numberOfFeatures;
        }
    }

    /**
     * Registered projection for an EPSG code that was explicitly declared, either
     * in the GeoJSON `crs` member or in the `epsg` option. Only reached when a
     * coordinate system really was declared, so a code that is not registered is
     * an error rather than a reason to fall back to the view projection —
     * silently reading foreign coordinates as view coordinates would place the
     * features somewhere else entirely.
     * @param epsg - EPSG code declared by the data or the caller.
     * @throws CRS is not specified neither in GeoJSON nor in the options parameter.
     */
    private getDeclaredProjection(epsg: string | number) {
        try {
            return this.GIS.Projection.getRegistered(epsg);
        } catch (error) {
            throw new Error('CRS is not specified neither in GeoJSON nor in the options parameter.');
        }
    }

    /**
     * Wire freshly added features so their area / border-length overlay
     * recalculates automatically after a later geometry change — e.g. when a
     * geometric operation (intersect, union, difference, clip, split) replaces
     * the geometry via `setGeometry`. This removes the need to refresh the
     * measurement manually after such operations.
     *
     * Only features on editable layers are wired: read-only layers never show a
     * measurement overlay, so this avoids attaching listeners across large
     * read-only datasets. Each per-feature listener is idempotent and only
     * recomputes features that actually carry measurement properties
     * (see {@link Edit.enableAutoMeasurement}).
     * @param layer - The layer the features were added to.
     * @param features - The features that were added.
     */
    private enableAutoMeasurement(layer: { get(key: string): unknown }, features: Feature<Geometry>[]): void {
        if (!layer.get('create') && !layer.get('modify')) return;
        for (const feature of features) {
            this.GIS.Edit.enableAutoMeasurement(feature);
        }
    }

    /**
     * Update feature geometry
     * Supporting only layers simple geometry at this moment
     * @param name - name of the layer
     * @param geometry - new geometry
     * @param id - value of the ID or attribute
     * @param options
     */
    public updateFeatureGeometryByID(name: string, geometry: Geometry | undefined, id: string | number, options?: GetIdPropertyOptions) {
        let idProperty = getIdProperty(options);

        const layer = this.getLayer(name);
        const source = layer.getSource();
        let updated = 0;
        source.forEachFeature((feature: Feature) => {
            this.checkIdAttribute(feature, idProperty);

            if (String(feature.get(idProperty)) === String(id)) {
                feature.setGeometry(geometry);
                updated++;
            }
        })

        if (updated === 0) {
            console.warn("Cannot find the feature.")
        }
    }

    /**
     * Get empty Openlayers geom
     * @param type - polygon or linestring at this moment
     * @param coordinates - array of coordinates
     * @param callback - function to be called after geometry is created
     * @param radius - {@link https://openlayers.org/en/latest/apidoc/module-ol_geom_Circle-Circle.html}
     * @returns Ol Geometry object {@link https://openlayers.org/en/latest/apidoc/module-ol_geom_SimpleGeometry-SimpleGeometry.html}
     */
    public createGeometry(
        type: GeometryType,
        coordinates:
            | Array<Array<Coordinate>>
            | Array<Array<Array<Coordinate>>>
            | Array<Coordinate>
            | Array<number>,
        callback?: GetGeometry,
        radius?: number
    ) {
        let geometry: Geometry | undefined;

        switch (type) {
            case 'polygon':
                geometry = new Polygon(coordinates as Array<Array<Coordinate>>);
                break;
            case 'multipolygon':
                geometry = new MultiPolygon(coordinates as Array<Array<Array<Coordinate>>>);
                break;
            case 'linestring':
                geometry = new LineString(coordinates as Array<Coordinate>);
                break;
            case 'multilinestring':
                geometry = new MultiLineString(coordinates as Array<Array<Coordinate>>);
                break;
            case 'circle':
                geometry = new Circle(coordinates as Coordinate, radius);
                break;
        }

        if (!geometry) throw Error('Geometry cannot be created.');

        if (callback) callback(geometry);
        return geometry;
    }

    /**
     *
     * @param name - name of the layer
     * @param id - value of the ID or attribute
     * @param options
     * @example GIS.Layer.removeFeatureByID('point', 1, {attribute: 'id'});
     * @returns number of removed features
     */
    public removeFeatureByID(name: string, id: string | number, options?: GetIdPropertyOptions) {
        let idProperty = getIdProperty(options);

        const layer = this.getLayer(name);
        const source = layer.getSource();

        const numberOfFeatures = source.getFeatures().length;
        let numberOfRemovedFeatures = 0;

        if (numberOfFeatures !== 0) {
            source.forEachFeature((feature: Feature) => {
                this.checkIdAttribute(feature, idProperty);

                if (String(feature.get(idProperty)) === String(id)) {
                    numberOfRemovedFeatures++;
                    source.removeFeature(feature);
                }
            })
        }

        return numberOfRemovedFeatures;
    }

    /**
     * Remove all features from the layer
     * @param name Layer name
     * @returns number of features
     */
    public removeFeatures(name: string) {
        const layer = this.getLayer(name);
        const source = layer.getSource();

        const numberOfFeatures = source.getFeatures().length;

        if (numberOfFeatures !== 0) {
            source.clear();
        }

        return numberOfFeatures;
    }

    /**
     * Get layer visibility
     * @param layerName Layer name
     * @returns visible true/false
     * @returns Visibility
     * @throws No such layer in the map.
     */
    public getLayerVisibility(layerName: string, callback?: Visible | undefined) {
        const layer = this.getLayer(layerName);
        if (callback) {
            callback(layer.getVisible());
        }
        return layer.getVisible();
    }

    /**
     * Get layer opacity
     * @param layerName Layer name
     * @param callback Opaque value: 0-1 where 0 is fully transparent and 1 is fully opaque.
     * @returns opacity Number between 0 and 1 {@link https://openlayers.org/en/latest/apidoc/module-ol_layer_VectorTile-VectorTileLayer.html#getOpacity}
     * @returns {number|callback} Opacity
     * @throws No such layer in the map.
     */
    public getLayerOpacity(layerName: string, callback?: Opacity | undefined) {
        const layer = this.getLayer(layerName);
        if (callback) {
            callback(layer.getOpacity());
        }
        return layer.getOpacity();
    }

    private checkBaseLayers(layer: BaseLayer, visible: boolean) {
        if (layer.get('base') && visible && !layer.getVisible()) {
            this.basemaps.map((basemap) => {
                basemap.setVisible(false);
                if ((basemap as LayerType & SwitcherActivation).switcher) {
                    (basemap as LayerType & SwitcherActivation).deactivateSwitcher();
                }
                if ((basemap as LayerType & Toggle).toggle) {
                    (basemap as LayerType & Toggle).deactivateToggle();
                }
                if ((basemap as LayerType & Legend).legend) {
                    (basemap as LayerType & Legend).hideLegend();
                }
            });
            return true;
        } else {
            if (layer.get('base') && !visible && layer.getVisible()) {
                console?.warn('Cannot switch off active base layer.');
                return false
            } else {
                return true;
            }
        }
    }

    /**
     * Set layer visibility
     * @param layerName Layer name
     * @param visible {boolean} true/false
     * @example Layer.setLayerVisibility('aerial', true);
     * @throws No such layer in the map.
     */
    public setLayerVisibility(layerName: string, visible: boolean) {
        const layer = this.getLayer(layerName);
        if (this.checkBaseLayers(layer, visible)) {
            if ((layer as LayerType & SwitcherActivation).switcher) {
                visible ? (layer as LayerType & SwitcherActivation).activateSwitcher() : (layer as LayerType & SwitcherActivation).deactivateSwitcher()
            }
            if ((layer as LayerType & Toggle).toggle) {
                visible ? (layer as LayerType & Toggle).activateToggle() : (layer as LayerType & Toggle).deactivateToggle()
            }
            if ((layer as LayerType & Legend).legend) {
                visible ? (layer as LayerType & Legend).showLegend() : (layer as LayerType & Legend).hideLegend()
            }
            layer.setVisible(visible);
        }
    }

    /**
     * Set layer extent
     * @param layerName Layer name
     * @param extent Extent array [minx, miny, maxx, maxy]
     * @param options - specify e.g. epsg
     * @example Layer.setLayerExtent('aerial', [3074754.30,8973952.07,3075712.95,8975406.02]);
     * @throws No such layer in the map.
     */
    public setLayerExtent(layerName: string, extent: Extent, options?: FormatAndEPSGOptions) {
        const layer = this.getLayer(layerName);

        if (extent.length !== 4 || !extent.every((e: unknown) => typeof e === 'number')) {
            throw Error('Invalid extent, should be array with 4 numbers.')
        }

        if (options && options.epsg) {
            const extentsProjection = this.GIS.Projection.getRegistered(options.epsg);
            extent = transformExtent(extent, extentsProjection, this.GIS.View.olObject.getProjection());
        }

        layer.setExtent(extent);
    }


    /**
     * @deprecated since version 1.1 - use {@link setLayerLoader} instead
     * This method is deprecated and will be removed in future releases.
     * @param {string} layerName - The name of the layer.
     * @param {string} url - The URL of the layer.
     */
    public setLayerUrl(layerName: string, url: string) {
        console.warn('setLayerUrl is deprecated. Use setUrl instead. This method will be removed in future releases.');

        // Redirect to the new method
        this.setUrl(layerName, url);
    }

    /**
     * Set layer's source url
     * @param layerName Layer name
     * @param url Url for accessing data by layer's source
     * @example Layer.setLayerUrl('aerial', '___BASEURL_BASEMAPS___/mapproxy1/wmts/{Layer}/{TileMatrixSet}/{TileMatrix}/{TileCol}/{TileRow}.jpeg');
     * @throws No such layer in the map.
     */
    public setUrl(layerName: string, url: string) {
        const layer = this.getLayer(layerName);
        const source = layer.getSource()
        source.setUrl(url);
    }

    /**
     * Set layer's source url
     * Current supporting only StaticImage
     * in params required are extent and url
     * @param layerName - Layer name
     * @param options - New source options
     */
    public setLayerNewSourceWithParams(layerName: string, options?: NewSourceOptions) {
        const layer = this.getLayer(layerName);
        if (layer instanceof ImageLayer) {
            const oldSource = layer.getSource();

            if (oldSource instanceof ImageStatic) {
                const {url, extent} = options ?? {};

                if (!url || !extent) {
                    console.error('URL and extent are required parameters for ImageStatic.');
                    return;
                }

                const newSource = new ImageStatic({
                    url: url,
                    imageExtent: extent,
                });

                layer.setSource(newSource);
            } else {
                console.error('The current source is not an ImageStatic.');
            }
        } else {
            console.error('The specified layer is not an ImageLayer.');
        }
    }

    /**
     * Set layer opacity
     * @param layerName Layer name
     * @param opacity Real number values from 0 to 1.
     * @example Layer.setLayerOpacity('aerial', 0.2);
     * @throws No such layer in the map.
     * @throws Opacity should be between 0 and 1.
     */
    public setLayerOpacity(layerName: string, opacity: number) {
        if (opacity < 0 || opacity > 1) {
            throw new Error('Opacity should be between 0 and 1.')
        }
        const layer = this.getLayer(layerName);
        layer.setOpacity(opacity);
    }

    /**
     * Mask raster layer based on vector layer
     * @param layerName Layer name of the raster layer to be masked
     * @param vectorLayerName Layer name of the vector layer used for masking
     * Works only for WMS layers!! It cannot be wmsTiled layer either.
     * This method listens to the 'prerender' event of the raster layer and applies a clipping path based on the geometries of the vector layer.
     */
    public maskRasterLayerBasedOnVectorLayer(layerName: string, vectorLayerName: string) {
        const layer = this.getLayer(layerName);
        const vectorLayer = this.getLayer(vectorLayerName) as VectorLayer<any>;

        layer.on('prerender', (event) => {
            const ctx = event.context;
            const frameState = event.frameState;
            if (!ctx || !frameState) return;

            const transform = frameState.coordinateToPixelTransform;
            if (!transform) return;

            const features = vectorLayer.getSource().getFeatures();
            if (!features || features.length === 0) return;

            if (!(ctx instanceof CanvasRenderingContext2D)) return;

            ctx.save();
            ctx.beginPath();

            for (const feature of features) {
                const geometry = feature.getGeometry();
                if (!geometry) continue;

                const type = geometry.getType();

                let rings: number[][][] = [];

                if (type === 'Polygon') {
                    rings = geometry.getCoordinates();
                } else if (type === 'MultiPolygon') {
                    const multiCoords = geometry.getCoordinates();
                    rings = multiCoords.flat();
                } else {
                    continue;
                }

                for (const ring of rings) {
                    ring.forEach(([x, y], index) => {
                        const pixel = this.getRenderPixel([x, y], this.GIS.Map.olObject, frameState.pixelRatio);

                        if (!pixel || isNaN(pixel[0]) || isNaN(pixel[1])) return;

                        if (index === 0) {
                            ctx.moveTo(pixel[0], pixel[1]);
                        } else {
                            ctx.lineTo(pixel[0], pixel[1]);
                        }
                    });
                    ctx.closePath();
                }
            }
            ctx.clip();
        });

        layer.on('postrender', (event) => {
            const ctx = event.context;
            if (ctx instanceof CanvasRenderingContext2D) {
                ctx.restore();
            }
        });
    }

    /**
     * Set layer style
     * @param name Layer name
     * @param style Style name
     * @example Layer.setLayerStyle('position', 'position_unavailable')
     * @throws No such layer in the map.
     * @throws No such style defined.
     */
    public setLayerStyle(name: string, style: string) {
        const olStyle = this.GIS.Style.getStyle(style);
        const layer: VectorLayer<any> = this.getLayer(name) as VectorLayer<any>;
        layer.setStyle(olStyle as StyleLike | undefined);
        layer.set(this.ACTIVE_STYLENAME, style);
    }

    /**
     * Get layer ZIndex
     * @param layerName Layer name
     * @param callback - function to be called after ZIndex is retrieved
     * @returns ZIndex Number higher number means higher layer {@link https://openlayers.org/en/latest/apidoc/module-ol_layer_VectorTile-VectorTileLayer.html#getOpacity}
     * @throws No such layer in the map.
     */
    public getZIndex(layerName: string, callback?: ZIndex) {
        const layer = this.getLayer(layerName);
        const zIndexValue = layer.getZIndex();

        if (callback) {
            callback(zIndexValue);
        }
        return zIndexValue;
    }

    /**
     * Set layer ZIndex
     * @param layerName Layer name
     * @param zIndex
     * @returns ZIndex Number higher number means higher layer {@link https://openlayers.org/en/latest/apidoc/module-ol_layer_VectorTile-VectorTileLayer.html#getOpacity}
     * @throws No such layer in the map.
     */
    public setZIndex(layerName: string, zIndex: string) {
        const layer = this.getLayer(layerName);
        layer.setZIndex(parseInt(zIndex, 10) || 0);
        return zIndex;
    }

    /**
     * @deprecated since version 1.1 - use {@link setLayerLoader} instead
     * This method is deprecated and will be removed in future releases.
     * @param {string} layerName - The name of the layer.
     * @param {string} auth - The authentication token.
     * @param {AuthenticationOptions} [options] - Authorization options, e.g. `{scheme: 'Bearer'}`.
     */
    public setLoaderAuthentication(layerName: string, auth: string, options?: AuthenticationOptions) {
        console.warn('setLoaderAuthentication is deprecated. Use setLayerLoader instead. This method will be removed in future releases.');

        // Redirect to the new method
        this.setLayerLoader(layerName, auth, options);
    }

    /**
     * Set layer authentication e.g. for geoserver
     * @param name Layer name
     * @param auth Credentials sent as the `Authorization` header value, without the scheme prefix
     * (Base64 user:password for `Basic`, the raw token for `Bearer`)
     * @param options Authorization options. `scheme` defaults to `Basic`, so existing calls
     * keep their behaviour.
     * @throws No such layer in the map.
     */
    public setLayerLoader(name: string, auth: string, options?: AuthenticationOptions) {
        const scheme = options?.scheme ?? this.DEFAULTAUTHENTICATIONSCHEME;
        const olLayer = this.getLayer(name);
        const source = olLayer.getSource();
        let loadFunction: LoadFunction | FeatureLoader;

        this.detachErrorHandlersFromSource(source);

        switch (true) {
            case source instanceof WMTS:
                loadFunction = this.createRasterLoadFunction(auth, name, scheme) as LoadFunction;
                source.setTileLoadFunction(loadFunction);
                break;
            case olLayer instanceof TileLayer:
                loadFunction = this.createRasterLoadFunction(auth, name, scheme) as LoadFunction;
                source.setTileLoadFunction(loadFunction);
                break;
            case olLayer instanceof ImageLayer:
                const olSource = olLayer.getSource();

                if (!(olSource instanceof ImageStatic)) {
                    loadFunction = this.createRasterLoadFunction(auth, name, scheme) as LoadFunction;
                    source.setImageLoadFunction(loadFunction);
                }
                break;
            case source instanceof VectorSource:
                loadFunction = this.createVectorLoadFunction(olLayer, source, auth, name, scheme) as FeatureLoader;
                source.setLoader(loadFunction);
                break;
            default:
                throw new Error('Could not determine layer type.')
        }
    }

    /**
     * @typedef NumberOfFailed
     * @property {number} raster_failed - An integer counter for failed requests still being retried.
     * @property {number} vector_failed - An integer counter for failed requests still being retried.
     * @property {number} raster_unfetched - An integer counter for raster requests the map gave up on.
     * @property {number} vector_unfetched - An integer counter for vector requests the map gave up on.
     */

    /**
     * Returns number of failed tiles caused by either server or network issues.
     *
     * `raster_failed` / `vector_failed` count only requests that are still being
     * retried, so the counters always settle and the hosting application can
     * wait for them — for example before printing:
     *
     * - Canceled requests are not failures and are never counted: a tile that
     *   left the viewport or was dropped by a source refresh is discarded by
     *   OpenLayers, so its request settles without the server ever answering.
     * - Failures that stopped being relevant are dropped: a tile of a zoom level
     *   or an area the map has moved away from is never requested again, so
     *   nothing would ever clear it.
     * - Requests the map gave up on move to `raster_unfetched` /
     *   `vector_unfetched`. They are reported through the `RequestFailed`
     *   callback and warned about in the console, and {@link fetchFailed} still
     *   re-requests them.
     *
     * @returns {Object<NumberOfFailed>} response
     */
    public getNumberOfFailed() {
        this.pruneIrrelevantFailures();
        return {
            raster_failed: Object.keys(this.rasterFailed).length,
            vector_failed: Object.keys(this.vectorFailed).length,
            raster_unfetched: Object.keys(this.rasterAbandoned).length,
            vector_unfetched: Object.keys(this.vectorAbandoned).length
        }
    }

    /**
     * Remove all failed raster and vector tiles
     */
    public removeFailedRasterAndVector() {
        this.rasterFailed = {};
        this.vectorFailed = {};
        this.rasterAbandoned = {};
        this.vectorAbandoned = {};
        this.rasterRetries = {};
        this.vectorRetries = {};
        this.notifiedFailures.clear();
        this.unfetchableWarned.clear();
        this.retryCycleExhausted = false;
        this.stopReconnectProbe();
    }

    /**
     * Register a Layer callback by name, mirroring the dispatch convention used
     * by {@link Map.setCallback} and {@link Interaction.setCallback}.
     *
     * Supported callbacks:
     * - `'RequestFailed'` — invoked whenever a layer request fails. The hosting
     *   application can use this to react to authentication errors (e.g.
     *   401/403) and refresh the bearer token before retrying. Deduplicated per
     *   failure burst: it fires once per layer + status combination and is
     *   re-armed when the layer recovers or when {@link fetchFailed} is called
     *   manually. A pan that fails 20 tiles of one layer therefore triggers a
     *   single invocation. Non-retryable failures such as 401/403 are reported
     *   right away and retired to `raster_unfetched` / `vector_unfetched` (see
     *   {@link getNumberOfFailed}) instead of being retried, so they never hold
     *   the pending counters up; {@link fetchFailed} re-requests them, which is
     *   what makes the refresh-the-token flow below work. Canceled requests are
     *   not reported: panning, zooming and refreshing a source discard the
     *   tiles the map no longer needs, which says nothing about the server.
     *
     * @param name - Name of the callback to register.
     * @param options - Reserved for future options (currently unused). Pass `{}`
     * or omit it.
     * @param callback - Function called with a {@link RequestFailedEvent}
     * object. Pass `null` (or omit it) to unregister.
     *
     * @example
     * GIS.Layer.setCallback('RequestFailed', {}, (event) => {
     *     console.log(event.status, event.url, event.layerName, event.type);
     *     if (event.status === 401 || event.status === 403) {
     *         refreshBearerToken().then(() => {
     *             GIS.Layer.fetchFailed();
     *         });
     *     }
     * });
     */
    public setCallback(name: 'RequestFailed', options?: object, callback?: RequestFailedCallback | null): void;
    public setCallback(name: string, _options?: object, callback?: RequestFailedCallback | null): void {
        switch (name) {
            case 'RequestFailed':
                this.requestFailedCallback = callback ?? null;
                break;
            default:
                console.warn(`[Layer.setCallback] Unknown callback name: ${name}`);
        }
    }

    /**
     * Notify the registered request-failed callback (if any).
     *
     * Deduplicated: the callback fires once per `type|layerName|status`
     * combination until the layer recovers, its failures are cleared, or a
     * manual {@link fetchFailed} re-arms notifications — so a burst of 20
     * failing tiles produces a single callback invocation.
     */
    private notifyRequestFailed(event: RequestFailedEvent): void {
        if (!this.requestFailedCallback) return;

        const key = `${event.type}|${event.layerName}|${event.status}`;
        if (this.notifiedFailures.has(key)) return;
        this.notifiedFailures.add(key);

        try {
            this.requestFailedCallback(event);
        } catch (error) {
            console.error('[Layer.notifyRequestFailed] Callback threw:', error);
        }
    }

    /**
     * Re-arm request-failed notifications for a layer once it recovers.
     */
    private clearNotifiedForLayer(layerName: string): void {
        if (!layerName || this.notifiedFailures.size === 0) return;
        const infix = `|${layerName}|`;
        this.notifiedFailures.forEach((key) => {
            if (key.includes(infix)) {
                this.notifiedFailures.delete(key);
            }
        });
    }

    /**
     * Clear tracked failures/retries just for a specific layer.
     * Optionally refreshes that layer's source.
     *
     * @param layerName - Name of the layer to clear failures for.
     * @param options
     * @param options.refresh - Refresh the layer's source after clearing (default: true).
     * @param options.stopWatcherIfEmpty - Stop the retry watcher if no failures remain (default: true).
     * @returns count of removed raster entries + whether vector was cleared
     */
    public clearFailuresByLayer(
        layerName: string,
        options: { stopWatcherIfEmpty?: boolean } = {}
    ) {
        const {stopWatcherIfEmpty = true} = options;

        this.getLayer(layerName);

        let removedRaster = 0;

        [this.rasterFailed, this.rasterAbandoned].forEach((collection) => {
            Object.keys(collection).forEach((key) => {
                const failed = collection[key] as
                    | (ImageTile & { [k: string]: any })
                    | (HTMLImageElement & { [k: string]: any });

                const ln = failed?.[this.IMAGELAYERNAMEPROPERTY];
                if (ln === layerName) {
                    delete collection[key];
                    delete this.rasterRetries[key];
                    this.unfetchableWarned.delete(key);
                    removedRaster++;
                }
            });
        });

        const hadVector = Boolean(
            this.vectorFailed[layerName] || this.vectorAbandoned[layerName] || this.vectorRetries[layerName]
        );
        delete this.vectorFailed[layerName];
        delete this.vectorAbandoned[layerName];
        delete this.vectorRetries[layerName];
        this.clearNotifiedForLayer(layerName);

        this.resetRetryCycleIfRecovered();

        if (stopWatcherIfEmpty) {
            const hasAnyRaster = Object.keys(this.rasterFailed).length > 0;
            const hasAnyVector = Object.keys(this.vectorFailed).length > 0;
            if (!hasAnyRaster && !hasAnyVector) {
                this.stopRetryWatcher();
            }
        }

        return {removedRaster, clearedVector: hadVector};
    }
}

/**
 * Event payload passed to the request-failed callback.
 */
export interface RequestFailedEvent {
    /** HTTP status code (e.g. 401, 403, 500). 0 when the request failed due to a network error. */
    status: number;
    /** The URL that was requested. */
    url: string;
    /** Name of the layer the request belongs to. */
    layerName: string;
    /** Whether this was a 'raster' or 'vector' request. */
    type: 'raster' | 'vector';
}

/**
 * Callback invoked when a layer tile/image/vector request fails.
 */
export type RequestFailedCallback = (event: RequestFailedEvent) => void;

export default Layer;
